import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Grid,
  Chip,
  Snackbar,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Paper,
  Button,
  Card,
  Collapse,
  Dialog,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  TableSortLabel,
  useTheme,
  useMediaQuery,
  Modal,
  Fade,
  ListItemText,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import Dbutton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import ThreeWayMatchTable from "./purchaseorder";
import PayAsYouGoDashboard from "./PayAsYouGoDashboard";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";
import DownloadIcon from "@mui/icons-material/Download";
import { CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import { format } from "date-fns";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TWMOverviewDashboard from "./TWMOverviewDashboard";
import { useOfflineTWM } from "../../../offline/hooks/useOfflineTWM";
import { twmRepository } from "../../../offline/repositories/twm.repository";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import deletePopupImg from "../../../assets/images/png/Group 2@2x.png";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import { consumePendingChatbotUpload } from "../../../components/Chatbot/chatTransferStore";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
  isDocumentUploadFile,
} from "../../../utils/documentUploadTypes";
import * as XLSX from "xlsx";
import {
  enterpriseBackButtonSx,
  enterpriseBodyTextSx,
  enterpriseBrowseButtonSx,
  ENTERPRISE_COLORS,
  enterprisePageSx,
  enterpriseSectionTitleSx,
  enterpriseStatusChipSx,
  enterpriseSupportingTextSx,
  getEnterpriseUploadZoneSx,
} from "../../../components/common/upload/enterpriseUploadStyles";

const TWM_REQUIRED_FILE_COUNT = 3;
const TWM_REQUIRED_DOCUMENT_SHORT_LABEL =
  "PO, GRN, Invoice & optional Payment Advice";
const TWM_ALLOWED_FILE_COUNT_LABEL = "at least three";
const TWM_UPLOAD_SLOT_LABELS = ["INVOICE", "GRN", "PO", "PAYMENT"];
const PAY_PER_USE_POLL_INTERVAL = 3000;
const PAY_PER_USE_TERMINAL_STATUSES = new Set([
  "COMPLETED",
  "COMPLETED_WITH_ERRORS",
  "FAILED",
  "ERROR",
  "CANCELLED",
]);
const PAY_PER_USE_SUCCESS_TERMINAL_STATUSES = new Set([
  "COMPLETED",
  "COMPLETED_WITH_ERRORS",
]);
const TWM_REQUIRED_DOCUMENTS = [
  {
    key: "invoice_data",
    label: "Invoice",
    fields: [
      "invoice_number",
      "invoice_no",
      "bill_number",
      "bill_no",
      "invoice_date",
      "date",
      "invoice_amount",
      "total_amount",
      "amount",
      "purchase_order_number",
      "po_number",
      "po_no",
      "vendor_name",
      "supplier_name",
      "line_items",
      "items",
    ],
  },
  {
    key: "grn_data",
    label: "GRN",
    fields: [
      "grn_number",
      "grn_no",
      "grn_date",
      "goods_receipt_no",
      "receipt_number",
      "challan_number",
      "challan_no",
      "order_number",
      "date",
      "received_date",
      "vendor",
      "vendor_name",
      "line_items",
      "items",
    ],
  },
  {
    key: "po_data",
    label: "PO",
    fields: [
      "purchase_order_no",
      "purchase_order_number",
      "po_number",
      "po_no",
      "order_number",
      "order_date",
      "po_date",
      "vendor",
      "vendor_name",
      "supplier_name",
      "line_items",
      "items",
    ],
  },
];

const normalizeFileName = (fileName) =>
  String(fileName || "")
    .trim()
    .toLowerCase();

const getDuplicateFileNames = (files = []) => {
  const seen = new Set();
  const duplicates = new Set();

  files.forEach((file) => {
    const normalizedName = normalizeFileName(file?.name);
    if (!normalizedName) return;
    if (seen.has(normalizedName)) {
      duplicates.add(file.name);
      return;
    }
    seen.add(normalizedName);
  });

  return Array.from(duplicates);
};

const hasMeaningfulExtractedValue = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(hasMeaningfulExtractedValue);
  if (typeof value === "object") {
    return Object.values(value).some(hasMeaningfulExtractedValue);
  }
  return String(value).trim() !== "";
};

const hasExtractedDocumentData = (documents, expectedFields = []) =>
  Array.isArray(documents) &&
  documents.some(
    (document) =>
      hasMeaningfulExtractedValue(document) &&
      expectedFields.some((field) =>
        hasMeaningfulExtractedValue(document?.[field]),
      ),
  );

const hasAllowedTwmFileCount = (count) =>
  count >= TWM_REQUIRED_FILE_COUNT;

const getTwmExtractionValidation = (data) => {
  const defaultMissingDocuments = TWM_REQUIRED_DOCUMENTS.map(
    ({ label }) => label,
  );
  if (!data || typeof data !== "object") {
    return {
      isValid: false,
      hasExpectedFileCount: false,
      failedFiles: [],
      missingDocuments: defaultMissingDocuments,
    };
  }

  const extractedFiles = Array.isArray(data.files) ? data.files : [];
  const failedFiles = extractedFiles.filter(
    (file) => {
      const status = String(file?.status ?? "")
        .trim()
        .toLowerCase();
      return status && !["ok", "success", "completed"].includes(status);
    },
  );
  const missingDocuments = TWM_REQUIRED_DOCUMENTS.reduce(
    (missing, { key, label, fields }) =>
      hasExtractedDocumentData(data[key], fields)
        ? missing
        : [...missing, label],
    [],
  );

  return {
    isValid:
      hasAllowedTwmFileCount(extractedFiles.length) &&
      failedFiles.length === 0 &&
      missingDocuments.length === 0,
    hasExpectedFileCount: hasAllowedTwmFileCount(extractedFiles.length),
    failedFiles,
    missingDocuments,
  };
};

const getTwmExtractionValidationMessage = ({
  hasExpectedFileCount,
  failedFiles = [],
  missingDocuments = [],
} = {}) => {
  if (!hasExpectedFileCount) {
    return `Upload must contain ${TWM_ALLOWED_FILE_COUNT_LABEL} valid documents, including Invoice, GRN, and PO. Payment Advice remains optional.`;
  }
  if (failedFiles.length > 0) {
    return "Upload completed, but extraction failed for one or more files. Please upload valid Invoice, GRN, PO, and optional Payment Advice documents.";
  }
  if (missingDocuments.length > 0) {
    return `Upload must include valid Invoice, GRN, and PO documents. Missing extracted data: ${missingDocuments.join(
      ", ",
    )}.`;
  }
  return "Upload must include valid Invoice, GRN, and PO documents.";
};

const hasSuccessfulTwmExtraction = (data) => {
  return getTwmExtractionValidation(data).isValid;
};

const LARGE_SNACK_MESSAGES = new Set([
  `Upload must contain ${TWM_ALLOWED_FILE_COUNT_LABEL} valid documents, including Invoice, GRN, and PO. Payment Advice remains optional.`,
]);

const normalizeMatchStatusText = (status) =>
  String(status || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();

const isSuccessfulMatchStatus = (status) => {
  if (status === true) return true;

  const normalizedStatus = normalizeMatchStatusText(status);
  if (!normalizedStatus) return false;

  if (
    normalizedStatus.includes("NOT MATCHED") ||
    normalizedStatus.includes("UNMATCHED") ||
    normalizedStatus.includes("MISMATCH") ||
    normalizedStatus.includes("REJECT") ||
    normalizedStatus.includes("FAILED") ||
    normalizedStatus.includes("EXCEPTION")
  ) {
    return false;
  }

  return (
    normalizedStatus === "MATCHED" ||
    normalizedStatus === "FULL MATCHED" ||
    normalizedStatus === "FULLMATCHED" ||
    normalizedStatus === "FULLY MATCHED" ||
    normalizedStatus === "FULLYMATCHED" ||
    normalizedStatus === "2 WAY MATCHED" ||
    normalizedStatus === "2WAY MATCHED" ||
    normalizedStatus === "TWO WAY MATCHED" ||
    normalizedStatus === "TWOWAY MATCHED" ||
    normalizedStatus.endsWith(" MATCHED") ||
    normalizedStatus.includes("ITEM MATCHED") ||
    normalizedStatus.includes("PRICE MATCHED") ||
    normalizedStatus.includes("AMOUNT MATCHED") ||
    normalizedStatus.includes("QUANTITY MATCHED") ||
    ["COMPLETED", "APPROVED", "SUCCESS", "SUCCESSFUL"].includes(
      normalizedStatus,
    )
  );
};

const getPayPerUseStatusTone = (status) => {
  const normalizedStatus = normalizeMatchStatusText(status);

  if (
    !normalizedStatus.includes("PARTIAL") &&
    isSuccessfulMatchStatus(normalizedStatus)
  ) {
    return "matched";
  }

  if (
    normalizedStatus.includes("PO MISSING") ||
    normalizedStatus.includes("INVOICE MISSING") ||
    normalizedStatus.includes("GRN MISSING") ||
    normalizedStatus.includes("MRN MISSING") ||
    normalizedStatus.includes("MISSING") ||
    normalizedStatus.includes("UNABLE TO MATCH") ||
    normalizedStatus.includes("UNVERIFIED")
  ) {
    return "missingDependency";
  }

  if (
    normalizedStatus.includes("NOT COMPARED") ||
    normalizedStatus.includes("NOT APPLICABLE")
  ) {
    return "notCompared";
  }

  if (
    normalizedStatus.includes("INCOMPLETE") ||
    normalizedStatus.includes("IN COMPLETE")
  ) {
    return "incomplete";
  }

  if (
    [
      "UNMATCHED",
      "FAILED",
      "REJECTED",
      "ERROR",
      "EXCEPTION",
      "MISMATCHED",
      "MISMATCH",
      "EXCEEDING",
      "MISSING",
    ].includes(normalizedStatus)
  ) {
    return "rejected";
  }

  if (
    normalizedStatus.includes("PARTIAL") ||
    [
      "QUEUED",
      "PROCESSING",
      "IN PROGRESS",
      "PENDING",
      "PARTIAL",
      "PARTIALLY MATCHED",
      "PARTIAL MATCH",
    ].includes(normalizedStatus)
  ) {
    return "partial";
  }

  return "default";
};

const formatPayPerUseStatusLabel = (status) => {
  if (status === true) return "Matched";
  if (status === false) return "Not Matched";

  const normalizedStatus = String(status || "")
    .trim()
    .toUpperCase();

  if (!normalizedStatus) return "Pending";

  return normalizedStatus
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getPayPerUseGroupFileByType = (group = {}, matcher) => {
  const resultJson = parsePayPerUseResultJson(group?.result_json);
  const rawFiles = Array.isArray(group?.files)
    ? group.files
    : Array.isArray(resultJson?.files)
      ? resultJson.files
      : [];
  const files = rawFiles
    .map((file) =>
      typeof file === "string"
        ? file
        : file?.file_name || file?.file || file?.name || "",
    )
    .filter(Boolean);
  return (
    files.find((fileName) => matcher(String(fileName || "").toUpperCase())) ||
    ""
  );
};

const parsePayPerUseResultJson = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return typeof value === "object" ? value : {};
};

const getPayPerUseFirstRecord = (value) => {
  if (Array.isArray(value)) return value[0] || {};
  return value && typeof value === "object" ? value : {};
};

const getPayPerUsePrimaryRecords = (record = {}) => {
  const resultJson = parsePayPerUseResultJson(
    record?.result_json || record?.extracted_data,
  );
  return {
    po: getPayPerUseFirstRecord(resultJson?.po_data),
    grn: getPayPerUseFirstRecord(resultJson?.grn_data),
    invoice: getPayPerUseFirstRecord(resultJson?.invoice_data),
  };
};

const getFirstDefinedDisplayValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const formatMatchingMode = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";

  return normalized
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const hasPayPerUseLineItems = (record = {}) =>
  Array.isArray(record?.line_items) && record.line_items.length > 0;

const inferPayPerUsePoNumber = (record = {}) => {
  const { po } = getPayPerUsePrimaryRecords(record);

  if (!hasPayPerUseLineItems(po)) {
    return "-";
  }

  return (
    getFirstDefinedDisplayValue(
      po?.purchase_order_no,
      po?.purchase_order_number,
      po?.po_number,
      po?.po_no,
      po?.order_number,
      Array.isArray(po?.purchase_order_candidates)
        ? po.purchase_order_candidates[0]
        : "",
      record?.po_number,
    ) || "-"
  );
};

const inferPayPerUseInvoiceNumber = (record = {}) => {
  const { invoice } = getPayPerUsePrimaryRecords(record);

  return (
    getFirstDefinedDisplayValue(
      invoice?.invoice_number,
      invoice?.invoice_no,
      invoice?.bill_number,
      invoice?.bill_no,
      Array.isArray(invoice?.invoice_number_candidates)
        ? invoice.invoice_number_candidates[0]
        : "",
      record?.invoice_number,
    ) || "-"
  );
};

const inferPayPerUseInvoiceTotal = (record = {}) => {
  const { invoice } = getPayPerUsePrimaryRecords(record);
  const matchingDetails = getPayPerUseMatchingDetails(record);

  return getFirstDefinedDisplayValue(
    invoice?.total_tax_included,
    invoice?.tax_included_total,
    invoice?.total_with_tax,
    invoice?.invoice_amount,
    invoice?.total_amount,
    invoice?.grand_total,
    invoice?.amount,
    matchingDetails?.amount_comparison?.invoice_total_amount,
    matchingDetails?.amount_comparison?.invoice_amount,
    record?.invoice_amount,
    record?.invoice_total_amount,
  );
};

const inferPayPerUseGrnNumber = (group = {}) => {
  const { grn: grnRecord } = getPayPerUsePrimaryRecords(group);
  const directValue =
    grnRecord?.order_number ||
    grnRecord?.grn_number ||
    grnRecord?.grn_no ||
    grnRecord?.goods_receipt_no ||
    grnRecord?.goods_receipt_number ||
    group?.grn_number ||
    group?.grn_no ||
    group?.goods_receipt_number ||
    group?.goods_receipt_no ||
    group?.mrn_number ||
    group?.receipt_number;

  if (directValue) {
    return directValue;
  }

  const grnFileName = getPayPerUseGroupFileByType(
    group,
    (fileName) => fileName.includes("MRN") || fileName.includes("GRN"),
  );

  if (!grnFileName) return "-";

  return grnFileName.replace(/\.pdf$/i, "").trim() || "-";
};

const getPayPerUseMatchingDetails = (group = {}) =>
  group?.matching_details ||
  parsePayPerUseResultJson(group?.result_json)?.matching_details ||
  {};

  const getPayPerUseBackendMatchStatus = (record = {}, statusKey) => {
  const matchingDetails = getPayPerUseMatchingDetails(record);
  const statusSummary = matchingDetails?.status_summary || {};
  const fallbackByKey = {
    item_status: matchingDetails?.overall_status,
    quantity_status: matchingDetails?.quantity_comparison?.status,
    amount_status: matchingDetails?.amount_comparison?.status,
  };
  const backendStatus = statusSummary?.[statusKey] || fallbackByKey[statusKey];

  if (!backendStatus) {
    return { status: "Pending", type: "partial" };
  }

  return {
    status: formatPayPerUseStatusLabel(backendStatus),
    type: getPayPerUseStatusTone(backendStatus),
  };
};

const getPayPerUseRecordStatuses = (record = {}) => ({
  item: getPayPerUseBackendMatchStatus(record, "item_status"),
  quantity: getPayPerUseBackendMatchStatus(record, "quantity_status"),
  price: getPayPerUseBackendMatchStatus(record, "amount_status"),
});

const getPayPerUseOverallMatchStatus = (record = {}) => {
  const backendStatus =
    getPayPerUseMatchingDetails(record)?.overall_status || record?.status;

  if (!backendStatus) {
    return { status: "Pending", type: "default" };
  }

  return {
    status: formatPayPerUseStatusLabel(backendStatus),
    type: getPayPerUseStatusTone(backendStatus),
  };
};

const getPayPerUsePreviewValue = (record = {}, keys = []) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "-";
};

const getExportValue = (record = {}, keys = []) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const getPaymentAdviceExportValues = (record = {}, resultJson = {}, matchingDetails = {}) => {
  const summary =
    record?.payment_advice_summary ||
    resultJson?.payment_advice_summary ||
    matchingDetails?.payment_advice_summary ||
    {};
  const paymentAdviceData = Array.isArray(record?.payment_advice_data)
    ? record.payment_advice_data
    : Array.isArray(resultJson?.payment_advice_data)
      ? resultJson.payment_advice_data
      : [];
  const paymentModes = Array.isArray(summary?.payment_modes)
    ? summary.payment_modes.join(", ")
    : Object.keys(summary?.payment_mode_breakup || {}).join(", ");
  const paymentReferences = Array.isArray(summary?.transaction_references)
    ? summary.transaction_references.join(", ")
    : Array.isArray(summary?.payment_advice_numbers)
      ? summary.payment_advice_numbers.join(", ")
      : "";
  const hasPaymentAdvice =
    Boolean(summary?.has_payment_advice) ||
    paymentAdviceData.length > 0 ||
    Number(summary?.payment_advice_count || 0) > 0;

  return {
    payment_advice_status: hasPaymentAdvice
      ? getExportValue(summary, ["payment_status", "status"]) || "AVAILABLE"
      : "NOT_AVAILABLE",
    payment_advice_amount: getExportValue(summary, [
      "payment_advice_total_amount",
      "paid_amount",
      "total_amount",
    ]),
    payment_advice_mode: paymentModes,
    payment_advice_reference: paymentReferences,
    payment_advice_count: getExportValue(summary, [
      "payment_advice_count",
      "record_count",
    ]) || (hasPaymentAdvice ? paymentAdviceData.length || 1 : 0),
    payment_advice_document_count: getExportValue(summary, [
      "payment_advice_document_count",
      "document_count",
    ]) || (hasPaymentAdvice ? paymentAdviceData.length || 1 : 0),
    payment_outstanding_amount: getExportValue(summary, [
      "outstanding_amount",
    ]),
    payment_coverage_percent: getExportValue(summary, [
      "payment_coverage_percent",
      "coverage_percent",
    ]),
  };
};

const getExportNestedValue = (record = {}, paths = []) => {
  for (const path of paths) {
    let current = record;
    let hasPath = true;
    for (const key of path) {
      if (current === undefined || current === null) {
        hasPath = false;
        break;
      }
      current = current[key];
    }
    if (
      hasPath &&
      current !== undefined &&
      current !== null &&
      String(current).trim() !== ""
    ) {
      return current;
    }
  }
  return "";
};

const getExportLineItems = (documentRecord = {}) =>
  Array.isArray(documentRecord?.line_items)
    ? documentRecord.line_items
    : Array.isArray(documentRecord?.items)
      ? documentRecord.items
      : [];

const getMatchingSupplierName = (record = {}) => {
  const normalizedRecord = normalizeTwmRecord(record);
  const resultJson = normalizedRecord?.result_json || {};
  const po = getPayPerUseFirstRecord(resultJson?.po_data);
  const grn = getPayPerUseFirstRecord(resultJson?.grn_data);
  const invoice = getPayPerUseFirstRecord(resultJson?.invoice_data);

  return getFirstDefinedDisplayValue(
    po?.vendor,
    po?.vendor_name,
    po?.supplier_name,
    invoice?.vendor_name,
    invoice?.supplier_name,
    grn?.vendor_name,
    grn?.vendor,
  );
};

const addSupplierOption = (optionsMap, value) => {
  const supplier = String(value || "").trim();
  if (!supplier || supplier === "-") return;
  optionsMap.set(supplier.toLowerCase(), supplier);
};

const getSupplierOptionsFromRecords = (records = []) => {
  const optionsMap = new Map();
  (Array.isArray(records) ? records : []).forEach((record) => {
    addSupplierOption(optionsMap, getMatchingSupplierName(record));
  });
  return Array.from(optionsMap.values()).sort((a, b) => a.localeCompare(b));
};

const getSupplierOptionsFromSummary = (summaryData = {}) => {
  const optionsMap = new Map();
  const vendorAnalytics = summaryData?.vendor_analytics || {};
  const invoiceAnalytics = summaryData?.invoice_analytics || {};
  const exceptionAnalytics = summaryData?.exception_analytics || {};

  [
    vendorAnalytics?.top_vendors_by_value,
    vendorAnalytics?.top_vendors_by_count,
    vendorAnalytics?.vendor_trend,
    invoiceAnalytics?.top_invoices,
    exceptionAnalytics?.vendor_wise_exceptions,
  ].forEach((source) => {
    const rows = Array.isArray(source)
      ? source
      : Array.isArray(source?.data)
        ? source.data
        : Array.isArray(source?.results)
          ? source.results
          : [];

    rows.forEach((row) => {
      addSupplierOption(
        optionsMap,
        row?.vendor || row?.vendor_name || row?.supplier_name,
      );
    });
  });

  return Array.from(optionsMap.values()).sort((a, b) => a.localeCompare(b));
};

const getExportDocMeta = (documentRecord = {}, source = "") => {
  if (source === "PO") {
    return {
      doc_no: getExportValue(documentRecord, [
        "purchase_order_no",
        "purchase_order_number",
        "po_number",
        "po_no",
        "order_number",
      ]),
      doc_date: getExportValue(documentRecord, ["order_date", "po_date"]),
    };
  }

  if (source === "GRN") {
    return {
      doc_no: getExportValue(documentRecord, [
        "order_number",
        "grn_number",
        "grn_no",
        "goods_receipt_no",
        "goods_receipt_number",
        "receipt_number",
      ]),
      doc_date: getExportValue(documentRecord, [
        "grn_bill_date",
        "bill_date",
        "grn_date",
        "received_date",
        "date",
        "created_at",
      ]),
    };
  }

  return {
    doc_no: getExportValue(documentRecord, [
      "invoice_number",
      "invoice_no",
      "bill_number",
      "bill_no",
    ]),
    doc_date: getExportValue(documentRecord, ["invoice_date", "date"]),
  };
};

const buildMatchingExportRows = (records = []) => {
  const exportRows = [];

  (Array.isArray(records) ? records : []).forEach((card) => {
    const normalizedCard = normalizeTwmRecord(card);
    const resultJson = normalizedCard?.result_json || {};
    const po = getPayPerUseFirstRecord(resultJson?.po_data);
    const grn = getPayPerUseFirstRecord(resultJson?.grn_data);
    const invoice = getPayPerUseFirstRecord(resultJson?.invoice_data);
      const matchingDetails = getPayPerUseMatchingDetails(normalizedCard);
    const statusSummary = matchingDetails?.status_summary || {};
    const recordStatuses = getPayPerUseRecordStatuses(normalizedCard);
    const approvalMeta = getTwmApprovalMeta(normalizedCard);
      const overallMatchStatus = getPayPerUseOverallMatchStatus(normalizedCard);
      const paymentAdviceValues = getPaymentAdviceExportValues(
        normalizedCard,
        resultJson,
        matchingDetails,
      );
    const vendorName = getFirstDefinedDisplayValue(
      po?.vendor,
      po?.vendor_name,
      po?.supplier_name,
      invoice?.vendor_name,
      invoice?.supplier_name,
      grn?.vendor_name,
      grn?.vendor,
    );
    const recordBase = {
      matching_id: normalizedCard.match_code || normalizedCard.twm_code || "",
      matching_type: formatMatchingMode(normalizedCard.matching_mode),
      supplier_name: vendorName,
      company_business_unit: getFirstDefinedDisplayValue(
        po?.business_unit,
        po?.company,
        grn?.business_unit,
        grn?.company,
        invoice?.business_unit,
        invoice?.company,
      ),
      location_plant: getFirstDefinedDisplayValue(
        po?.location,
        po?.plant,
        grn?.location,
        grn?.plant,
        invoice?.location,
        invoice?.plant,
      ),
      po_number: inferPayPerUsePoNumber(normalizedCard),
      po_date: getExportValue(po, ["order_date", "po_date"]),
      invoice_number: inferPayPerUseInvoiceNumber(normalizedCard),
      invoice_date: getExportValue(invoice, ["invoice_date", "date"]),
      grn_number: inferPayPerUseGrnNumber(normalizedCard),
      grn_date: getExportValue(grn, [
        "grn_bill_date",
        "bill_date",
        "grn_date",
        "received_date",
        "date",
        "created_at",
      ]),
      po_grand_total: getExportValue(po, [
        "total_tax_included",
        "tax_included_total",
        "total_with_tax",
        "grand_total",
        "total_amount",
        "amount",
      ]),
      po_total_tax_excluded: getExportValue(po, [
        "total_tax_excluded",
        "tax_excluded_total",
        "total_without_tax",
        "taxable_amount",
        "subtotal",
      ]),
      po_total_tax_included: getExportValue(po, [
        "total_tax_included",
        "tax_included_total",
        "total_with_tax",
        "grand_total",
        "total_amount",
      ]),
      invoice_grand_total: inferPayPerUseInvoiceTotal(normalizedCard),
      invoice_total_tax_excluded: getExportValue(invoice, [
        "total_tax_excluded",
        "tax_excluded_total",
        "total_without_tax",
        "taxable_amount",
        "subtotal",
      ]),
      invoice_total_tax_included: getExportValue(invoice, [
        "total_tax_included",
        "tax_included_total",
        "total_with_tax",
        "grand_total",
        "invoice_amount",
        "total_amount",
      ]),
      overall_status: overallMatchStatus.status,
      po_status: getExportValue(statusSummary, [
        "po_status",
        "purchase_order_status",
      ]) || getExportValue(po, ["status", "po_status", "approval_status"]) ||
        getExportValue(normalizedCard, ["po_status", "purchase_order_status"]) ||
        overallMatchStatus.status,
      invoice_status: getExportValue(statusSummary, [
        "invoice_status",
        "bill_status",
      ]) || getExportValue(invoice, ["status", "invoice_status", "approval_status"]) ||
        getExportValue(normalizedCard, ["invoice_status", "bill_status"]) ||
        overallMatchStatus.status,
      grn_status: getExportValue(statusSummary, [
        "grn_status",
        "mrn_status",
      ]) || getExportValue(grn, ["status", "grn_status", "approval_status"]) ||
        getExportValue(normalizedCard, ["grn_status", "mrn_status"]) ||
        overallMatchStatus.status,
      ...paymentAdviceValues,
      item_status: recordStatuses.item.status,
      quantity_status: recordStatuses.quantity.status,
      price_status: recordStatuses.price.status,
      matching_date: normalizedCard?.created_at || "",
      approval_status: approvalMeta.label,
    };
    const documentGroups = [
      { source: "PO", documentRecord: po },
      { source: "INVOICE", documentRecord: invoice },
      { source: "GRN", documentRecord: grn },
    ];

    documentGroups.forEach(({ source, documentRecord }) => {
      const docMeta = getExportDocMeta(documentRecord, source);
      const lineItems = getExportLineItems(documentRecord);

      if (!lineItems.length) {
        exportRows.push({
          ...recordBase,
          source,
          document_number: docMeta.doc_no,
          document_date: docMeta.doc_date,
        });
        return;
      }

      lineItems.forEach((lineItem, index) => {
        exportRows.push({
          ...recordBase,
          source,
          line_no: index + 1,
          document_number: docMeta.doc_no,
          document_date: docMeta.doc_date,
          part_no: getExportValue(lineItem, [
            "part_number",
            "part_no",
            "item_code",
            "cat_code",
            "code",
          ]),
          description: getExportValue(lineItem, [
            "description",
            "description_of_goods",
            "item_name",
            "item_description",
          ]),
          hsn: getExportValue(lineItem, ["hsn_code", "hsn"]),
          quantity: getExportValue(lineItem, [
            "qty",
            "qty.",
            "quantity",
            "parsed_qty",
            "received_q",
            "received_quantity",
            "quantity_received",
            "billed-base-qty",
          ]),
          uom:
            getExportValue(lineItem, ["uom"]) ||
            getExportNestedValue(lineItem, [
              ["extra_fields", "extra_fields", "uom"],
            ]),
          unit_price: getExportValue(lineItem, [
            "rate",
            "unit_price",
            "parsed_rate",
          ]),
          tax_percent: getExportValue(lineItem, [
            "tax_percent",
            "gst_percent",
            "tax_percentage",
            "tax_rate",
            "igst_rate",
          ]),
          tax_type: getExportValue(lineItem, [
            "tax_type",
            "gst_type",
            "tax_category",
          ]),
          tax_amount: getExportValue(lineItem, ["tax_amount", "gst_amount"]),
          amount: getExportValue(lineItem, [
            "line_amount",
            "amount",
            "taxable_amount",
            "taxable",
            "line_total",
            "parsed_amount",
          ]),
          lot_number: getExportValue(lineItem, ["lot_number", "batch_number"]),
          expiry_date: getExportValue(lineItem, ["expiry_date", "batch/exp"]),
          remarks: getExportValue(lineItem, [
            "remarks",
            "remark",
            "exception_notes",
            "notes",
          ]),
        });
      });
    });
  });

  return exportRows;
};

const MATCHING_EXPORT_HEADERS = [
  { key: "matching_id", label: "Matching ID" },
  { key: "matching_type", label: "Matching Type" },
  { key: "supplier_name", label: "Supplier Name" },
  { key: "company_business_unit", label: "Company / Business Unit" },
  { key: "location_plant", label: "Location / Plant" },
  { key: "po_number", label: "PO Number" },
  { key: "po_date", label: "PO Date" },
  { key: "invoice_number", label: "Invoice Number" },
  { key: "invoice_date", label: "Invoice Date" },
  { key: "grn_number", label: "GRN Number" },
  { key: "grn_date", label: "GRN Date" },
  { key: "quantity", label: "Quantity" },
  { key: "uom", label: "UOM" },
  { key: "unit_price", label: "Unit Price" },
  { key: "tax_percent", label: "Tax" },
  { key: "tax_type", label: "Tax Type" },
  { key: "tax_amount", label: "Tax Amount" },
  { key: "amount", label: "Amount" },
  { key: "item_status", label: "Item Status" },
  { key: "quantity_status", label: "Quantity Status" },
  { key: "price_status", label: "Price Status" },
  { key: "overall_status", label: "Overall Status" },
  { key: "po_status", label: "PO Status" },
  { key: "invoice_status", label: "Invoice Status" },
  { key: "grn_status", label: "GRN Status" },
  { key: "payment_advice_status", label: "Payment Advice Status" },
  { key: "payment_advice_amount", label: "Payment Advice Amount" },
  { key: "payment_advice_mode", label: "Payment Mode" },
  { key: "payment_advice_reference", label: "Payment Advice Reference" },
  { key: "payment_advice_count", label: "Payment Advice Count" },
  { key: "payment_advice_document_count", label: "Payment Advice Documents" },
  { key: "payment_outstanding_amount", label: "Payment Outstanding Amount" },
  { key: "payment_coverage_percent", label: "Payment Coverage %" },
  { key: "po_grand_total", label: "PO Grand Total" },
  { key: "po_total_tax_excluded", label: "PO Total Tax Excluded" },
  { key: "po_total_tax_included", label: "PO Total Tax Included" },
  { key: "invoice_grand_total", label: "Invoice Grand Total" },
  {
    key: "invoice_total_tax_excluded",
    label: "Invoice Total Tax Excluded",
  },
  {
    key: "invoice_total_tax_included",
    label: "Invoice Total Tax Included",
  },
  { key: "matching_date", label: "Matching Date" },
  { key: "approval_status", label: "Approval Status" },
  { key: "source", label: "Source" },
  { key: "line_no", label: "Line No" },
  { key: "document_number", label: "Document Number" },
  { key: "document_date", label: "Document Date" },
  { key: "part_no", label: "Part No" },
  { key: "description", label: "Description" },
  { key: "hsn", label: "HSN" },
  { key: "lot_number", label: "Lot / Batch Number" },
  { key: "expiry_date", label: "Expiry / Batch" },
  { key: "remarks", label: "Remarks" },
];

const MATCHING_AMOUNT_EXPORT_KEYS = new Set([
  "unit_price",
  "tax_amount",
  "amount",
  "po_grand_total",
  "po_total_tax_excluded",
  "po_total_tax_included",
  "invoice_grand_total",
  "invoice_total_tax_excluded",
  "invoice_total_tax_included",
  "payment_advice_amount",
  "payment_outstanding_amount",
]);

const formatExportAmountWithSeparators = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "";
  }

  const raw = String(value).trim();
  const normalized = raw.replace(/[^\d.-]+/g, "");
  const numberValue = Number(normalized);

  if (!normalized || !Number.isFinite(numberValue)) {
    return raw;
  }

  const hasDecimals = /\.\d+/.test(normalized);
  return numberValue.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const normalizeMatchingSummaryResponse = (payload) => {
  let summary = payload;

  if (typeof summary === "string") {
    try {
      summary = JSON.parse(summary);
    } catch {
      return {};
    }
  }

  if (!summary || typeof summary !== "object" || Array.isArray(summary)) {
    return {};
  }

  if (
    summary.overview ||
    summary.matching_analytics ||
    summary.dashboard_metrics
  ) {
    return summary;
  }

  const nestedSummary = summary.data || summary.result || summary.response;
  if (nestedSummary && typeof nestedSummary === "object" && !Array.isArray(nestedSummary)) {
    return normalizeMatchingSummaryResponse(nestedSummary);
  }

  return summary;
};

const formatMatchingExportCellValue = (row, header) => {
  const value = row?.[header.key] ?? "";
  if (MATCHING_AMOUNT_EXPORT_KEYS.has(header.key)) {
    return formatExportAmountWithSeparators(value);
  }
  return value;
};

const downloadMatchingWorkbook = (records = [], fileName) => {
  const rows = buildMatchingExportRows(records);
  const worksheetRows = rows.map((row) =>
    MATCHING_EXPORT_HEADERS.reduce((sheetRow, header) => {
      sheetRow[header.label] = formatMatchingExportCellValue(row, header);
      return sheetRow;
    }, {}),
  );
  const worksheet = XLSX.utils.json_to_sheet(worksheetRows, {
    header: MATCHING_EXPORT_HEADERS.map((header) => header.label),
  });
  worksheet["!cols"] = MATCHING_EXPORT_HEADERS.map((header) => {
    const maxContentLength = worksheetRows.reduce((maxLength, row) => {
      const valueLength = String(row?.[header.label] ?? "").length;
      return Math.max(maxLength, valueLength);
    }, header.label.length);
    return {
      wch: Math.min(Math.max(maxContentLength + 3, 14), 42),
    };
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Matching Report");
  XLSX.writeFile(workbook, fileName);
  return rows.length;
};

const formatPayPerUseAmount = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "-";
  }

  const numericValue = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return `\u20B9 ${numericValue.toLocaleString("en-IN")}`;
};

const getPayPerUseNestedValue = (record = {}, path = []) => {
  let current = record;
  for (const key of path) {
    if (current === undefined || current === null) {
      return undefined;
    }
    current = current[key];
  }
  return current;
};

const buildPayPerUsePreviewRows = (items = [], type = "") => {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    id: index + 1,
    partNo:
      getPayPerUsePreviewValue(item, [
        "part_number",
        "part_no",
        "item_code",
        "cat_code",
        "code",
      ]) || "-",
    description: getPayPerUsePreviewValue(item, [
      "description",
      "description_of_goods",
      "item_name",
    ]),
    qty: getPayPerUsePreviewValue(item, [
      "qty",
      "qty.",
      "quantity",
      "received_q",
      "received_quantity",
      "billed-base-qty",
      "quantity_received",
    ]),
    uom:
      getPayPerUsePreviewValue(item, ["uom"]) ||
      getPayPerUseNestedValue(item, ["extra_fields", "extra_fields", "uom"]) ||
      "-",
    rate: formatPayPerUseAmount(
      getPayPerUsePreviewValue(item, ["rate", "unit_price", "parsed_rate"]),
    ),
    amount: formatPayPerUseAmount(
      getPayPerUsePreviewValue(item, [
        "line_amount",
        "amount",
        "taxable_amount",
        "taxable",
        "line_total",
        "parsed_amount",
      ]),
    ),
    extra:
      type === "GRN"
        ? getPayPerUsePreviewValue(item, [
            "lot_number",
            "batch_number",
            "expiry_date",
          ])
        : type === "INVOICE"
          ? getPayPerUsePreviewValue(item, [
              "batch/exp",
              "batch_number",
              "packing",
            ])
          : getPayPerUsePreviewValue(item, [
              "hsn_code",
              "igst_rate",
              "tax_rate",
    ]),
  }));
};

const getPayPerUseResultsPayload = (payload = {}) => {
  if (Array.isArray(payload)) {
    return { groups: payload };
  }
  if (payload?.results && !Array.isArray(payload.results)) {
    return payload.results;
  }
  if (payload?.data?.results && !Array.isArray(payload.data.results)) {
    return payload.data.results;
  }
  if (payload?.data && !Array.isArray(payload.data)) {
    return payload.data;
  }
  return payload || {};
};

const getPayPerUseResultGroups = (payload = {}) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.results?.groups)) {
    return payload.results.groups;
  }
  if (Array.isArray(payload?.groups)) {
    return payload.groups;
  }
  if (Array.isArray(payload?.data?.results?.groups)) {
    return payload.data.results.groups;
  }
  if (Array.isArray(payload?.data?.groups)) {
    return payload.data.groups;
  }
  if (Array.isArray(payload?.results)) {
    return payload.results;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

// Status Chip Component for main table
const StatusChip = ({ status, type }) => {
  const config = {
    missingDependency: {
      color: "#7C2D12",
      bgcolor: "#FFF7ED",
      border: "#FDBA74",
    },
    incomplete: { color: "#B45309", bgcolor: "#FEF3C7", border: "#F59E0B" },
    notCompared: { color: "#334155", bgcolor: "#F8FAFC", border: "#E2E8F0" },
    matched: { color: "#2E7D32", bgcolor: "#E8F5E9", icon: "âœ“" },
    partial: { color: "#ED6C02", bgcolor: "#FFF3E0", icon: "âš " },
    rejected: { color: "#D32F2F", bgcolor: "#FFEBEE", icon: "âœ—" },
    default: { color: "#757575", bgcolor: "#FAFAFA", icon: "?" },
  };

  const { color, bgcolor, border } = config[type] || config.default;
  const chipBorder = border || `${color}20`;
  const statusIcon =
    type === "matched" ? (
      <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
    ) : type === "partial" ||
      type === "incomplete" ||
      type === "notCompared" ||
      type === "missingDependency" ? (
      <AccessTimeIcon sx={{ fontSize: 16, color }} />
    ) : type === "rejected" ? (
      <CancelIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
    ) : (
      <AccessTimeIcon sx={{ fontSize: 16, color }} />
    );

  return (
    <Chip
      icon={statusIcon}
      label={status}
      size="small"
      sx={{
        fontFamily: "gilroyMedium",
        fontSize: "14px",
        fontWeight: 400,
        height: "28px",
        backgroundColor: bgcolor,
        color: color,
        border: `1px solid ${chipBorder}`,
        "& .MuiChip-icon": {
          marginLeft: "8px",
          marginRight: "-4px",
        },
        "& .MuiChip-label": {
          px: 0.9,
          fontWeight: 400,
        },
      }}
    />
  );
};

const getQuantityComparisonStatus = ({
  poQty,
  grnQty,
  invQty,
  hasPo,
  hasGrn,
  hasInv,
}) => {
  const normalizedPo = Number(poQty) || 0;
  const normalizedGrn = Number(grnQty) || 0;
  const normalizedInv = Number(invQty) || 0;

  if (!hasPo && !hasGrn && !hasInv) {
    return { status: "No Data", type: "rejected" };
  }

  if (
    hasPo &&
    hasGrn &&
    hasInv &&
    normalizedPo === normalizedGrn &&
    normalizedPo === normalizedInv
  ) {
    return { status: "Matched", type: "matched" };
  }

  const comparePair = (left, right) => {
    if (left > right) return { status: "Rejected", type: "rejected" };
    if (left < right) return { status: "Partial", type: "partial" };
    return { status: "Matched", type: "matched" };
  };

  if (hasGrn && hasInv) return comparePair(normalizedInv, normalizedGrn);
  if (hasPo && hasInv) return comparePair(normalizedInv, normalizedPo);
  if (hasPo && hasGrn) return comparePair(normalizedGrn, normalizedPo);

  return { status: "No Data", type: "rejected" };
};

// Helper to analyze quantity match status
const analyzeQuantityMatch = (poData, grnData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length || (!grnItems.length && !invoiceItems.length)) {
      return { status: "No Data", type: "rejected" };
    }

    let matched = 0;
    let partial = 0;
    let rejected = 0;
    let totalChecked = 0;

    poItems.forEach((poItem) => {
      const poPartNo = poItem.part_no || poItem.item_code;
      const poQty = parseFloat(poItem.qty || poItem.parsed_qty || 0);

      const grnItem = grnItems.find(
        (item) => item.part_no === poPartNo || item.item_code === poPartNo,
      );

      const invoiceItem = invoiceItems.find(
        (item) =>
          item.part_no === poPartNo ||
          item.cat_code?.toString().includes(poPartNo) ||
          item.description_of_goods?.includes(poPartNo),
      );

      const grnQty = parseFloat(
        grnItem?.quantity_received ||
          grnItem?.received_quantity ||
          grnItem?.received_q ||
          grnItem?.qty ||
          0,
      );
      const invQty = parseFloat(
        invoiceItem?.qty || invoiceItem?.parsed_qty || 0,
      );

      const comparisonStatus = getQuantityComparisonStatus({
        poQty,
        grnQty,
        invQty,
        hasPo: true,
        hasGrn: Boolean(grnItem),
        hasInv: Boolean(invoiceItem),
      });

      if (comparisonStatus.type === "matched") matched++;
      else if (comparisonStatus.type === "rejected") rejected++;
      else partial++;

      totalChecked++;
    });

    if (rejected > 0) return { status: "Rejected", type: "rejected" };
    if (partial > 0 && rejected === 0)
      return { status: "Partial", type: "partial" };
    if (matched === totalChecked && totalChecked > 0)
      return { status: "Matched", type: "matched" };

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

// Helper to analyze price match status
const analyzePriceMatch = (poData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length || !invoiceItems.length) {
      return { status: "No Data", type: "rejected" };
    }

    let matched = 0;
    let partial = 0;
    let rejected = 0;
    let totalChecked = 0;

    poItems.forEach((poItem) => {
      const poPartNo = poItem.part_no || poItem.item_code;
      const poPrice = parseFloat(poItem.rate || poItem.parsed_rate || 0);

      const invoiceItem = invoiceItems.find(
        (item) =>
          item.part_no === poPartNo ||
          item.cat_code?.toString().includes(poPartNo) ||
          item.description_of_goods?.includes(poPartNo),
      );

      // If item not found â†’ PARTIAL (not rejected)
      if (!invoiceItem) {
        partial++;
      } else {
        const invPrice = parseFloat(
          invoiceItem.rate || invoiceItem.parsed_rate || 0,
        );

        // ðŸ”´ REJECTED: Price INCREASED in invoice (greater than)
        if (invPrice > poPrice) {
          rejected++;
        }
        // ðŸŸ¡ PARTIAL: Price DECREASED in invoice (smaller than)
        else if (invPrice < poPrice) {
          partial++;
        }
        // âœ… MATCHED: Same price
        else {
          matched++;
        }
      }

      totalChecked++;
    });

    if (rejected > 0) return { status: "Rejected", type: "rejected" };
    if (partial > 0 && rejected === 0)
      return { status: "Partial", type: "partial" };
    if (matched === totalChecked && totalChecked > 0)
      return { status: "Matched", type: "matched" };

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

// Helper to analyze item match status
const analyzeItemMatch = (poData, grnData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length) {
      return { status: "No PO Items", type: "rejected" };
    }

    const poItemCount = poItems.length;
    const grnItemCount = grnItems.length;
    const invoiceItemCount = invoiceItems.length;

    // ðŸ”´ REJECTED: GRN has MORE items than PO (EXCEEDING)
    if (grnItemCount > poItemCount) {
      return { status: "Rejected", type: "rejected" };
    }

    // ðŸ”´ REJECTED: Invoice has MORE items than PO (EXCEEDING)
    if (invoiceItemCount > poItemCount) {
      return { status: "Rejected", type: "rejected" };
    }

    // ðŸŸ¡ PARTIAL: GRN has LESS items than PO
    if (grnItemCount < poItemCount) {
      return { status: "Partial", type: "partial" };
    }

    // ðŸŸ¡ PARTIAL: Invoice has LESS items than PO
    if (invoiceItemCount < poItemCount) {
      return { status: "Partial", type: "partial" };
    }

    // âœ… MATCHED: All item counts match
    if (poItemCount === grnItemCount && poItemCount === invoiceItemCount) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

const getMatchStatus = (result_json) => {
  try {
    const poItems = result_json?.po_data || [];
    const grnItems = result_json?.grn_data || [];
    const invoiceItems = result_json?.invoice_data || [];

    const normalize = (v) => String(v).trim().toLowerCase();

    let allMatched = true;

    poItems.forEach((poItem) => {
      const code = normalize(poItem?.part_no || poItem?.item_code);

      const grn = grnItems.find(
        (g) => normalize(g?.part_no || g?.item_code) === code,
      );
      const inv = invoiceItems.find(
        (i) => normalize(i?.part_no || i?.item_code) === code,
      );

      const poQty = parseFloat(poItem?.qty || 0);
      const grnQty = parseFloat(
        grn?.quantity_received ||
          grn?.received_quantity ||
          grn?.received_q ||
          grn?.qty ||
          0,
      );
      const invQty = parseFloat(inv?.qty || 0);

      if (
        isNaN(poQty) ||
        isNaN(grnQty) ||
        isNaN(invQty) ||
        poQty !== grnQty ||
        poQty !== invQty
      ) {
        allMatched = false;
      }
    });

    return allMatched ? "Matched" : "Partially Matched";
  } catch {
    return "Partially Matched";
  }
};

const DownloadMenuButton = ({ onCSV }) => {
  return (
    <Tooltip
      title="Export XLSX"
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: "13px",
            fontWeight: 500,
            px: 1.4,
            py: 0.9,
            borderRadius: 1.5,
          },
        },
      }}
    >
      <IconButton
        onClick={onCSV}
        sx={{
          width: 34,
          height: 34,
          p: 0,
          border: "1px solid #dbeafe",
          borderRadius: 1.5,
          backgroundColor: "#f8fbff",
          color: "#1d4ed8",
          "&:hover": {
            backgroundColor: "#eff6ff",
            borderColor: "#bfdbfe",
          },
        }}
      >
        <DownloadIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );
};

const EmptyState = ({ title = "No records found", subtitle }) => (
  <Box sx={{ textAlign: "center", py: 8, px: 2, color: "text.secondary" }}>
    <AssignmentTurnedInIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, fontFamily: "GilroyBold", fontSize: "18px" }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="body2"
        sx={{ mt: 0.5, fontFamily: "gilroyMedium", fontSize: "14px" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const TableLoadingState = ({ message = "Loading records..." }) => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(248, 250, 252, 0.72)",
      backdropFilter: "blur(1px)",
      pointerEvents: "none",
    }}
  >
    <Paper
      elevation={0}
      sx={{
        minWidth: 220,
        px: 2.5,
        py: 1.75,
        borderRadius: 2,
        border: "1px solid #dbeafe",
        backgroundColor: "#ffffff",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.4}>
        <CircularProgress size={22} thickness={4} />
        <Typography
          sx={{
            fontFamily: "gilroyMedium",
            fontSize: "14px",
            fontWeight: 600,
            color: "#334155",
          }}
        >
          {message}
        </Typography>
      </Stack>
    </Paper>
  </Box>
);

const getTwmDocumentArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
};

const getTwmLineItemCount = (items) =>
  getTwmDocumentArray(items).reduce(
    (count, document) => count + (Array.isArray(document?.line_items) ? document.line_items.length : 0),
    0,
  );

const buildTwmInsightRows = (
  poData,
  grnData,
  invoiceData,
  paymentAdviceData,
  isFourWay,
) => {
  const rows = [
    {
      label: "PO",
      documents: getTwmDocumentArray(poData).length,
      lines: getTwmLineItemCount(poData),
      metricLabel: "line item",
      color: "#2563eb",
    },
    {
      label: "GRN",
      documents: getTwmDocumentArray(grnData).length,
      lines: getTwmLineItemCount(grnData),
      metricLabel: "line item",
      color: "#0891b2",
    },
    {
      label: "Invoice",
      documents: getTwmDocumentArray(invoiceData).length,
      lines: getTwmLineItemCount(invoiceData),
      metricLabel: "line item",
      color: "#16a34a",
    },
  ];

  if (isFourWay) {
    const paymentAdviceDocuments = getTwmDocumentArray(paymentAdviceData).length;
    rows.push({
      label: "Payment Advice",
      documents: paymentAdviceDocuments,
      lines: paymentAdviceDocuments,
      metricLabel: "document",
      color: "#7c3aed",
    });
  }

  return rows;
};

const getTwmAdvancePaymentValue = (
  invoiceData,
  paymentAdviceData,
  matchingDetails,
) => {
  const invoice = getTwmDocumentArray(invoiceData)[0] || {};
  const paymentAdvice = getTwmDocumentArray(paymentAdviceData)[0] || {};
  const paymentSummary = matchingDetails?.payment_advice_summary || {};

  return getFirstDefinedDisplayValue(
    invoice?.advance_paid,
    invoice?.advance_payment,
    invoice?.advance_amount,
    paymentAdvice?.advance_paid,
    paymentAdvice?.advance_payment,
    paymentAdvice?.advance_amount,
    paymentSummary?.advance_payment_amount,
    paymentSummary?.advance_paid,
  );
};

const getTwmStatusTone = (status = "") => {
  const normalized = String(status).toLowerCase();
  if (normalized.includes("match") && !normalized.includes("no")) {
    return { bg: "#ecfdf5", color: "#047857", border: "#bbf7d0" };
  }
  if (normalized.includes("partial") || normalized.includes("pending")) {
    return { bg: "#fffbeb", color: "#b45309", border: "#fde68a" };
  }
  return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
};

const MatchResultInsight = ({
  poData,
  grnData,
  invoiceData,
  paymentAdviceData,
  matchingDetails,
  expectedDocumentCount = 3,
}) => {
  const hasPaymentAdvice = getTwmDocumentArray(paymentAdviceData).length > 0;
  const isFourWay =
    hasPaymentAdvice ||
    Number(expectedDocumentCount) >= 4 ||
    String(matchingDetails?.matching_mode || "")
      .toUpperCase()
      .includes("FOUR");
  const rows = buildTwmInsightRows(
    poData,
    grnData,
    invoiceData,
    paymentAdviceData,
    isFourWay,
  );
  const maxLines = Math.max(...rows.map((row) => row.lines), 1);
  const itemStatus = analyzeItemMatch(poData, grnData, invoiceData);
  const quantityStatus = analyzeQuantityMatch(poData, grnData, invoiceData);
  const priceStatus = analyzePriceMatch(poData, invoiceData);
  const advancePaymentValue = getTwmAdvancePaymentValue(
    invoiceData,
    paymentAdviceData,
    matchingDetails,
  );
  const formattedAdvancePayment =
    advancePaymentValue === ""
      ? "-"
      : formatPayPerUseAmount(advancePaymentValue);
  const overallStatus =
    matchingDetails?.overall_status ||
    matchingDetails?.status ||
    (itemStatus.type === "matched" && quantityStatus.type === "matched" && priceStatus.type === "matched"
      ? "Matched"
      : "Needs Review");
  const missingDocs = rows.filter((row) => row.documents === 0).map((row) => row.label);
  const responseText = missingDocs.length
    ? `${missingDocs.join(", ")} response data is not available yet. The table below shows every extracted field received so far.`
    : `All required document responses are available. Review item, quantity, price${isFourWay ? ", and payment advice" : ""} status before saving.`;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2.5,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: "1px solid #dbe3ef",
        backgroundColor: "#ffffff",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography sx={{ fontFamily: "GilroyBold", fontSize: "18px", color: "#0f172a" }}>
              {isFourWay ? "4-Way Match Response" : "3-Way Match Response"}
            </Typography>
            <Typography sx={{ mt: 0.4, fontFamily: "gilroyMedium", fontSize: "14px", color: "#64748b" }}>
              {responseText}
            </Typography>
          </Box>
          <Chip
            label={overallStatus}
            sx={{
              height: 34,
              fontFamily: "GilroyBold",
              fontSize: "14px",
              border: `1px solid ${getTwmStatusTone(overallStatus).border}`,
              color: getTwmStatusTone(overallStatus).color,
              backgroundColor: getTwmStatusTone(overallStatus).bg,
            }}
          />
        </Stack>

        <Grid container spacing={1.5}>
          {rows.map((row) => (
            <Grid item xs={12} md={isFourWay ? 3 : 4} key={row.label}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  height: "100%",
                  borderRadius: 2,
                  borderColor: "#e2e8f0",
                  backgroundColor: "#f8fafc",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography sx={{ fontFamily: "GilroyBold", fontSize: "14px", color: "#0f172a" }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontFamily: "gilroyMedium", fontSize: "13px", color: "#64748b" }}>
                    {row.documents} response{row.documents === 1 ? "" : "s"}
                  </Typography>
                </Stack>
                <Box sx={{ mt: 1.5, height: 10, borderRadius: 999, backgroundColor: "#e5e7eb", overflow: "hidden" }}>
                  <Box
                    sx={{
                      width: `${Math.max(8, Math.round((row.lines / maxLines) * 100))}%`,
                      height: "100%",
                      borderRadius: 999,
                      backgroundColor: row.color,
                    }}
                  />
                </Box>
                <Typography sx={{ mt: 1, fontFamily: "gilroyMedium", fontSize: "14px", color: "#334155" }}>
                  {row.lines} {row.metricLabel}
                  {row.lines === 1 ? "" : "s"} extracted
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {[
            ["Item", itemStatus.status],
            ["Quantity", quantityStatus.status],
            ["Price", priceStatus.status],
            ...(isFourWay
              ? [["Advance Payment", formattedAdvancePayment]]
              : []),
          ].map(([label, value]) => (
            <Chip
              key={label}
              label={`${label}: ${value}`}
              sx={{
                minHeight: 34,
                fontFamily: "gilroyMedium",
                fontSize: "14px",
                border: `1px solid ${getTwmStatusTone(value).border}`,
                color: getTwmStatusTone(value).color,
                backgroundColor: getTwmStatusTone(value).bg,
              }}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

// Date Format
const formatDate = (date) => {
  if (!date) return "—";

  let day, month, year;

  // DD/MM/YYYY or DD-MM-YYYY
  if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/.test(date)) {
    const parts = date.includes("/") ? date.split("/") : date.split("-");
    [day, month, year] = parts.map(Number);
  }
  // YYYY-M-D or YYYY-MM-DD
  else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(date)) {
    [year, month, day] = date.split("-").map(Number);
  }
  // Try JS Date fallback
  else {
    const parsed = new Date(date);
    if (isNaN(parsed)) return "—";
    day = parsed.getDate();
    month = parsed.getMonth() + 1;
    year = parsed.getFullYear();
  }

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthName = monthNames[month - 1];
  const dayStr = day.toString().padStart(2, "0");

  return `${dayStr}-${monthName}-${year}`;
};

const normalizeTwmRecord = (record = {}) => {
  const twmMeta = record?.three_way_matching || {};
  const parseResultJson = (value) => {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return typeof value === "object" ? value : {};
  };
  const resultJson = parseResultJson(
    record?.result_json ||
      record?.extracted_data ||
      record?.three_way_matching_data ||
      twmMeta?.result_json ||
      twmMeta?.extracted_data ||
      twmMeta?.three_way_matching_data,
  );

  return {
    ...record,
    token: record?.token || twmMeta?.token || record?.twm_token || "",
    match_code:
      record?.match_code || record?.twm_code || twmMeta?.twm_code || "",
    twm_code: record?.twm_code || record?.match_code || twmMeta?.twm_code || "",
    matching_mode:
      record?.matching_mode ||
      record?.matchingMode ||
      twmMeta?.matching_mode ||
      twmMeta?.matchingMode ||
      "",
    result_json: resultJson,
    status: record?.status || twmMeta?.status || "",
    approval_status: record?.approval_status ?? twmMeta?.approval_status ?? null,
    your_status: record?.your_status ?? twmMeta?.your_status ?? null,
    role: record?.role || twmMeta?.role || "",
    created_at:
      record?.created_at ||
      twmMeta?.created_at ||
      record?.created_on ||
      record?.created_date ||
      "",
    approvers: record?.approvers || twmMeta?.approvers || [],
  };
};

// Updated ApprovalStatusTooltip component
const ApprovalStatusTooltip = ({ approvers }) => {
  if (!approvers || approvers.length === 0) return null;

  const approved = approvers.filter((a) => a.status === "APPROVED");
  const pending = approvers.filter((a) => a.status === "PENDING");
  const rejected = approvers.filter((a) => a.status === "REJECTED");

  return (
    <Box sx={{ p: 1.5, minWidth: 220 }}>
      {/* âœ… Approved */}
      {approved.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: "#0bf063ff",
              fontSize: "13px",
            }}
          >
          Approved ({approved.length})
          </Typography>
          {approved.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#f0fdf4",
                mb: 0.5,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, color: "#166534", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#308350ff", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/*Pending */}
      {pending.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: "#e98b1fff",
              fontSize: "13px",
            }}
          >
            Pending ({pending.length})
          </Typography>
          {pending.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#fefce8",
                mb: 0.5,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 16, color: "#854d0e", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#713f12", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* âŒ Rejected */}
      {rejected.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "#991b1b", fontSize: "13px" }}
          >
            âŒ Rejected ({rejected.length})
          </Typography>
          {rejected.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#fef2f2",
                mb: 0.5,
              }}
            >
              <CancelIcon sx={{ fontSize: 16, color: "#991b1b", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#7f1d1d", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const getApproversArray = (card = {}) =>
  Array.isArray(card?.approvers)
    ? card.approvers
    : card?.approvers
      ? Object.entries(card.approvers).map(([id, status]) => ({
          id,
          status,
          name: id,
        }))
      : [];

const getTwmApprovalMeta = (card = {}) => {
  const approvers = getApproversArray(card);

  if (approvers.length > 0) {
    const allApproved = approvers.every((a) => a.status === "APPROVED");
    const anyRejected = approvers.some((a) => a.status === "REJECTED");

    if (allApproved) {
      return { label: "Approved", value: "approved", color: "success", approvers };
    }
    if (anyRejected) {
      return { label: "Rejected", value: "rejected", color: "error", approvers };
    }
    return { label: "Pending", value: "pending", color: "warning", approvers };
  }

  const rawStatus = String(
    card?.approval_status || card?.your_status || "",
  ).toLowerCase();

  if (rawStatus.includes("approve")) {
    return { label: "Approved", value: "approved", color: "success", approvers };
  }
  if (rawStatus.includes("reject")) {
    return { label: "Rejected", value: "rejected", color: "error", approvers };
  }
  if (rawStatus.includes("pending")) {
    return { label: "Pending", value: "pending", color: "warning", approvers };
  }

  return { label: "No Status", value: "none", color: "default", approvers };
};

const ThreeWayMatching = ({
  uploadmode,
  onModeChange,
  isPayPerUse = false,
}) => {
  const loginData = useSelector((state) => state.loginData.value);
  const [auth_token] = useState(loginData?.auth_token);
  const [user_id] = useState(loginData?.user_id);

  const [user_token] = useState(loginData?.user?.user_token);
  const orgToken =
    loginData?.organization_token || loginData?.org_token || loginData?.user?.organization_token || user_token;
  const [user_designation] = useState(loginData?.user?.designation);
  const user_role = loginData?.user?.role;

  // UI states
  const [mode, setMode] = useState(uploadmode);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [response, setResponse] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [overallProgress, setOverallProgress] = useState(0);
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });
  const [isDragging, setIsDragging] = useState(false);

  const [getmatchdata, setMatchedData] = useState(null);
  const [matchedresponse, setMatchedResponse] = useState(null);
  const [summaryResponse, setSummaryResponse] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [savedTwmCode, setSavedTwmCode] = useState("");
  const [successDialogMessage, setSuccessDialogMessage] = useState(
    "Your Accounts Payable Automation record was successfully saved.",
  );
  const [approvedTokens, setApprovedTokens] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewItemsDialog, setViewItemsDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedPayPerUseGroup, setSelectedPayPerUseGroup] = useState(null);

  const [nextTwmCode, setNextTwmCode] = useState("");
  const [payPerUseStatus, setPayPerUseStatus] = useState(null);
  const [payPerUseResults, setPayPerUseResults] = useState(null);
  const [payPerUseBatchToken, setPayPerUseBatchToken] = useState("");
  const [payPerUsePaginationData, setPayPerUsePaginationData] = useState(null);
  const [payPerUseListLoading, setPayPerUseListLoading] = useState(false);
  const [tableTransitionLoading, setTableTransitionLoading] = useState(false);

  // Pagination is scoped to the current Matching page visit.
  const [pageNumber, setPageNumber] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("all");
  const tableTransitionTimerRef = useRef(null);
  const payPerUseListRequestRef = useRef(0);
  const statusFilterReadyRef = useRef(false);
  const selectedSuppliers = useMemo(
    () => (selectedSupplier ? [selectedSupplier] : []),
    [selectedSupplier],
  );
  const apiSearchTerm = useMemo(
    () =>
      selectedSuppliers.length
        ? selectedSuppliers.join(",")
        : String(searchTerm || "").trim(),
    [searchTerm, selectedSuppliers],
  );
  const apiStatusFilter = useMemo(
    () =>
      approvalStatusFilter === "all"
        ? "all"
        : String(approvalStatusFilter || "").trim().toUpperCase(),
    [approvalStatusFilter],
  );

  const [openOfflinePopup, setOpenOfflinePopup] = useState(false);

  const showTableTransitionLoader = useCallback(() => {
    setTableTransitionLoading(true);
    if (tableTransitionTimerRef.current) {
      clearTimeout(tableTransitionTimerRef.current);
    }
    tableTransitionTimerRef.current = setTimeout(() => {
      setTableTransitionLoading(false);
      tableTransitionTimerRef.current = null;
    }, 180);
  }, []);

  const handleTableSort = (key) => {
    showTableTransitionLoader();
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const renderTableSortLabel = (key, label) => (
    <TableSortLabel
      active={sortConfig.key === key}
      direction={sortConfig.key === key ? sortConfig.direction : "asc"}
      onClick={() => handleTableSort(key)}
      IconComponent={UnfoldMoreIcon}
      sx={{
        "& .MuiTableSortLabel-icon": {
          opacity: 1,
          color: "inherit !important",
        },
        "&.MuiTableSortLabel-root": {
          color: "inherit",
        },
        "&.MuiTableSortLabel-root:hover": {
          color: "inherit",
          backgroundColor: "transparent",
        },
        "&.MuiTableSortLabel-root.Mui-focusVisible": {
          backgroundColor: "transparent",
        },
      }}
    >
      {label}
    </TableSortLabel>
  );

  const getSortValue = (record = {}, key) => {
    if (key === "match_code") {
      return String(record?.match_code || record?.twm_code || "").toLowerCase();
    }
    if (key === "created_at") {
      const timestamp = new Date(record?.created_at || 0).getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    }
    return "";
  };

  // Sort current page data (backend pagination supplies 1 page at a time)
  const getSortedData = () => {
    return [...filterRecords].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue !== bValue) {
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "desc"
            ? bValue - aValue
            : aValue - bValue;
        }
        return sortConfig.direction === "desc"
          ? String(bValue).localeCompare(String(aValue))
          : String(aValue).localeCompare(String(bValue));
      }

      const aCode = a?.match_code || "";
      const bCode = b?.match_code || "";
      return bCode.localeCompare(aCode);
    });
  };

  // Refs for upload
  const controllerRef = useRef(null);
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingChatAutoUploadRef = useRef(false);
  const handleUploadRef = useRef(null);
  const payPerUsePollRef = useRef(null);
  const payPerUseResultsFetchRef = useRef(false);

  const fetchNextCode = async () => {
    try {
      const { data } = await axios.post(
        API_ENDPOINT.peek_next_twm_code,
        { user_token },
        { headers: { Authorization: `Bearer ${auth_token}` } },
      );
      setNextTwmCode(data?.next_match_code || "");
    } catch {
      setNextTwmCode("");
    }
  };

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, []);

  const scrollByAmount = (dx) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  const listColumnWidths = {
    matchCode: 180,
    matchingType: 130,
    supplierName: 220,
    poId: 150,
    poDate: 145,
    invoiceNumber: 165,
    invoiceDate: 145,
    grnNumber: 150,
    grnDate: 145,
    paymentRefNo: 165,
    advancePaid: 145,
    balanceDue: 145,
    poTotal: 155,
    invoiceTotal: 165,
    quantityStatus: 150,
    priceStatus: 140,
    itemStatus: 135,
    matchingDate: 145,
    approvalStatus: 150,
    approveReject: 150,
    action: 100,
  };

  const resetUploadState = (options = {}) => {
    const { abortUpload = true } = options;

    if (abortUpload && controllerRef.current) {
      try {
        controllerRef.current.abort();
      } catch {
        // ignore
      }
      controllerRef.current = null;
    }

    pendingChatAutoUploadRef.current = false;
    setIsDragging(false);
    setSelectedFiles([]);
    setResponse(null);
    setUploadStatus("idle");
    setOverallProgress(0);
    setPayPerUseStatus(null);
    setPayPerUseResults(null);
    setPayPerUseBatchToken("");
    payPerUseResultsFetchRef.current = false;

    if (payPerUsePollRef.current) {
      clearInterval(payPerUsePollRef.current);
      payPerUsePollRef.current = null;
    }

    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = "";
      } catch {
        // ignore
      }
    }
  };

  const addSelectedFiles = useCallback(
    (incomingFiles) => {
      const files = Array.from(incomingFiles || []);
      if (!files.length) return;

      const invalidFiles = files.filter((file) => !isDocumentUploadFile(file));
      const documentFiles = files.filter(isDocumentUploadFile);

      if (invalidFiles.length > 0) {
        setSnack({
          open: true,
          type: "error",
          msg: "Only supported document files are allowed.",
        });
      }

      if (!documentFiles.length) return;

      const duplicateFileNames = getDuplicateFileNames([
        ...selectedFiles,
        ...documentFiles,
      ]);
      if (duplicateFileNames.length > 0) {
        setSnack({
          open: true,
          type: "error",
          msg: "Same files are not allowed.",
        });
        return;
      }

      setSelectedFiles((prev) => [...prev, ...documentFiles]);
      setResponse(null);
      setUploadStatus("idle");
      setOverallProgress(0);
      setPayPerUseStatus(null);
      setPayPerUseResults(null);
    },
    [isPayPerUse, selectedFiles],
  );

  useEffect(() => {
    if (uploadmode && ["main", "upload", "view"].includes(uploadmode)) {
      setMode(uploadmode);
    }
  }, [uploadmode]);

  const go = (next) => {
    if (mode === "upload" && next !== "upload") {
      resetUploadState({ abortUpload: true });
    }
    if (typeof onModeChange === "function") onModeChange(next);
    else setMode(next);
  };

  useEffect(() => {
    if (mode !== "upload" || !dropRef.current) return;
    const el = dropRef.current;
    const onDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    const onDragLeave = () => setIsDragging(false);
    const onDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) {
        addSelectedFiles(files);
      }
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [mode, addSelectedFiles]);

  // File upload handlers
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    addSelectedFiles(files);
  };
  const handleRemoveFile = (fileIndex) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    setResponse(null);
    setUploadStatus("idle");
    setOverallProgress(0);
    setPayPerUseStatus(null);
    setPayPerUseResults(null);
    setPayPerUseBatchToken("");
    payPerUseResultsFetchRef.current = false;
    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = "";
      } catch {
        // ignore
      }
    }
  };

  const handleCancelUpload = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      setUploadStatus("idle");
      setOverallProgress(0);
      setSnack({ open: true, type: "info", msg: "Upload cancelled." });
    }
  };

  const handleUpload = async () => {
    if (
      selectedFiles.length === 0 ||
      uploadStatus === "uploading" ||
      uploadStatus === "processing"
    ) {
      return;
    }

    if (getDuplicateFileNames(selectedFiles).length > 0) {
      setSnack({
        open: true,
        type: "error",
        msg: "Same file name is not allowed. Please rename the documents and upload again.",
      });
      return;
    }

    if (!isPayPerUse && !hasAllowedTwmFileCount(selectedFiles.length)) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please select at least 3 documents including Invoice, GRN, and PO. Payment Advice is optional.",
      });
      return;
    }

    if (!selectedFiles.every(isDocumentUploadFile)) {
      setSnack({
        open: true,
        type: "error",
        msg: "Only supported document files are allowed.",
      });
      return;
    }

    setUploadStatus("uploading");
    setOverallProgress(0);
    setResponse(null);

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));
    formData.append("org_token", orgToken);
    if (isPayPerUse) {
      formData.append("user_token", user_token);
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const uploadEndpoint = isPayPerUse
        ? API_ENDPOINT.bulk_three_way_matching
        : API_ENDPOINT.extractdoc;

      const res = await axios.post(uploadEndpoint, formData, {
        headers: {
          Authorization: `Bearer ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setOverallProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });

      if (isPayPerUse) {
        const payload = res?.data || {};
        payPerUseResultsFetchRef.current = false;
        setPayPerUseBatchToken(payload?.batch_token || "");
        setPayPerUseStatus(payload);
        setPayPerUseResults(null);
        setUploadStatus("processing");
        setOverallProgress(100);
        setSnack({
          open: true,
          type: "success",
          msg: "Bulk extraction started successfully.",
        });
        return;
      }

      const extractedData = res?.data || {};
      const extractionValidation = getTwmExtractionValidation(extractedData);
      const hasValidExtraction = extractionValidation.isValid;
      setResponse(extractedData);
      setSnack({
        open: true,
        type: hasValidExtraction ? "success" : "warning",
        msg: hasValidExtraction
          ? "Files uploaded & extracted successfully."
          : getTwmExtractionValidationMessage(extractionValidation),
      });
      setUploadStatus(hasValidExtraction ? "success" : "error");
      setOverallProgress(100);
    } catch (err) {
      if (axios.isCancel?.(err)) return;
      setSnack({
        open: true,
        type: "error",
        msg: err?.response?.data?.error || "Upload failed. Please try again.",
      });
      setUploadStatus("error");
    } finally {
      controllerRef.current = null;
    }
  };
  handleUploadRef.current = handleUpload;
  console.log(matchedresponse);
  // Search handlers
  const filterRecords = (matchedresponse || []).filter((item) => {
    const q = String(apiSearchTerm || "").trim().toLowerCase();
    const safeLower = (v) => String(v ?? "").toLowerCase();
    const result = item?.result_json || {};
    const apiSearchEnabled =
      typeof navigator !== "undefined" && navigator.onLine;
    const localSearchMatches =
      !q ||
      safeLower(item?.match_code).includes(q) ||
      safeLower(item?.matching_mode).includes(q) ||
      safeLower(formatMatchingMode(item?.matching_mode)).includes(q) ||
      (result?.po_data || []).some((po) =>
        safeLower(
          po?.vendor || po?.vendor_name || po?.supplier_name,
        ).includes(q),
      ) ||
      (result?.grn_data || []).some((grn) =>
        safeLower(
          grn?.vendor_name || grn?.vendor || grn?.receiver_name,
        ).includes(q),
      ) ||
      (result?.invoice_data || []).some((invoice) =>
        safeLower(invoice?.vendor_name || invoice?.supplier_name).includes(q),
      ) ||
      (result?.po_data || []).some((poNO) =>
        safeLower(
          poNO?.purchase_order_no ||
            poNO?.purchase_order_number ||
            poNO?.po_number ||
            poNO?.po_no,
        ).includes(q),
      ) ||
      (result?.invoice_data || []).some((invoiceNO) =>
        safeLower(invoiceNO?.invoice_number).includes(q),
      ) ||
      (result?.grn_data || []).some((grnNO) =>
        safeLower(
          grnNO?.order_number ||
            grnNO?.grn_number ||
            grnNO?.grn_no ||
            grnNO?.goods_receipt_no ||
            grnNO?.goods_receipt_number ||
            grnNO?.receipt_number,
        ).includes(q),
      );
    const matchesSearch = apiSearchEnabled ? true : localSearchMatches;

    return matchesSearch;
  });

  const supplierOptions = useMemo(() => {
    const optionsMap = new Map();
    getSupplierOptionsFromRecords(matchedresponse || []).forEach((supplier) =>
      addSupplierOption(optionsMap, supplier),
    );
    getSupplierOptionsFromSummary(summaryResponse || {}).forEach((supplier) =>
      addSupplierOption(optionsMap, supplier),
    );
    selectedSuppliers.forEach((supplier) =>
      addSupplierOption(optionsMap, supplier),
    );
    return Array.from(optionsMap.values()).sort((a, b) => a.localeCompare(b));
  }, [matchedresponse, selectedSuppliers, summaryResponse]);

  const handleSearchChange = (value) => {
    showTableTransitionLoader();
    setPageNumber(1);
    setSelectedSupplier("");
    setSearchTerm(value);
  };

  const handleSupplierChange = (value) => {
    showTableTransitionLoader();
    setPageNumber(1);
    setSearchTerm("");
    setSelectedSupplier(value || "");
  };

  const removeSupplierFilter = useCallback(
    () => {
      showTableTransitionLoader();
      setPageNumber(1);
      setSearchTerm("");
      setSelectedSupplier("");
    },
    [showTableTransitionLoader],
  );

  useEffect(() => {
    if (!statusFilterReadyRef.current) {
      statusFilterReadyRef.current = true;
      return;
    }
    showTableTransitionLoader();
    setPageNumber(1);
  }, [approvalStatusFilter, showTableTransitionLoader]);

  useEffect(
    () => () => {
      if (tableTransitionTimerRef.current) {
        clearTimeout(tableTransitionTimerRef.current);
      }
    },
    [],
  );

  // Upload status chip
  const statusChip = useMemo(() => {
    switch (uploadStatus) {
      case "uploading":
        return (
          <Chip
            label={`Uploading ${overallProgress}%`}
            color="info"
            size="small"
            sx={{ fontSize: "12px" }}
          />
        );
      case "success":
        return (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="Success"
            color="success"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
      case "processing":
        return (
          <Chip
            label={`${
              payPerUseStatus?.status || "Processing"
            } ${Math.round(overallProgress || 0)}%`}
            color="warning"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
      case "error":
        return (
          <Chip
            label="Error"
            color="error"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
      default:
        return (
          <Chip
            label="Waiting to upload"
            variant="outlined"
            sx={enterpriseStatusChipSx}
            size="medium"
          />
        );
    }
  }, [overallProgress, payPerUseStatus?.status, uploadStatus]);

  const canSaveMatching = useMemo(
    () => hasSuccessfulTwmExtraction(response),
    [response],
  );
  const payPerUseResultPayload = useMemo(
    () => getPayPerUseResultsPayload(payPerUseResults),
    [payPerUseResults],
  );
  const payPerUseGroupedResults = useMemo(
    () => getPayPerUseResultGroups(payPerUseResults),
    [payPerUseResults],
  );
  const payPerUseTerminalStatus = String(
    payPerUseResults?.status || payPerUseStatus?.status || "",
  )
    .trim()
    .toUpperCase();
  const payPerUseHasCompletedStatus =
    PAY_PER_USE_SUCCESS_TERMINAL_STATUSES.has(payPerUseTerminalStatus);
  const canSavePayPerUseMatching = useMemo(() => {
    return (
      isPayPerUse &&
      payPerUseHasCompletedStatus &&
      payPerUseGroupedResults.length > 0
    );
  }, [isPayPerUse, payPerUseGroupedResults.length, payPerUseHasCompletedStatus]);

  // File size display
  const formatKB = (bytes) => `${(bytes / 1024).toFixed(2)} KB`;
  const payPerUseBoardVisibleLimit = 20;
  const payPerUseVisibleBoardFiles = isPayPerUse
    ? selectedFiles.slice(0, payPerUseBoardVisibleLimit)
    : [];
  const payPerUseRemainingStackCount = isPayPerUse
    ? Math.max(selectedFiles.length - payPerUseVisibleBoardFiles.length, 0)
    : 0;
  const payPerUseVisibleBoardCount = payPerUseVisibleBoardFiles.length;
  const payPerUseBoardColumns = Math.min(
    Math.max(payPerUseVisibleBoardCount, 1),
    5,
  );
  const payPerUseBoardRows = Math.max(
    Math.ceil(payPerUseVisibleBoardCount / 5),
    1,
  );
  const payPerUseCardWidth =
    payPerUseVisibleBoardCount <= 3
      ? { xs: "132px", sm: "156px", md: "176px" }
      : payPerUseVisibleBoardCount <= 5
        ? { xs: "122px", sm: "144px", md: "162px" }
        : payPerUseVisibleBoardCount <= 10
          ? { xs: "100px", sm: "118px", md: "134px" }
          : payPerUseVisibleBoardCount <= 15
            ? { xs: "90px", sm: "106px", md: "120px" }
            : { xs: "84px", sm: "98px", md: "112px" };
  const payPerUseBoardMinHeight =
    payPerUseBoardRows === 1
      ? { xs: 230, sm: 270, md: 305 }
      : payPerUseBoardRows === 2
        ? { xs: 320, sm: 370, md: 420 }
        : payPerUseBoardRows === 3
          ? { xs: 400, sm: 470, md: 520 }
          : { xs: 485, sm: 560, md: 620 };
  const payPerUseGridTemplateColumns = {
    xs: `repeat(${payPerUseBoardColumns}, minmax(0, ${payPerUseCardWidth.xs}))`,
    sm: `repeat(${payPerUseBoardColumns}, minmax(0, ${payPerUseCardWidth.sm}))`,
    md: `repeat(${payPerUseBoardColumns}, minmax(0, ${payPerUseCardWidth.md}))`,
  };

  const handleSaveMatching = async () => {
    if (isPayPerUse) {
      const groups = payPerUseGroupedResults;
      const batchToken =
        payPerUseResults?.batch_token ||
        payPerUseStatus?.batch_token ||
        payPerUseBatchToken;

      if (!batchToken) {
        setSnack({
          open: true,
          type: "warning",
          msg: "Batch token is missing. Please retry the extraction.",
        });
        return;
      }

      if (!groups.length) {
        setSnack({
          open: true,
          type: "warning",
          msg: "No grouped results are available to save yet.",
        });
        return;
      }

      try {
        const saveResponses = [];

        for (const group of groups) {
          if (!group?.twm_code) {
            continue;
          }

          const { data } = await axios.post(
            API_ENDPOINT.save_bulk_three_way_matching,
            {
              user_token,
              batch_token: batchToken,
              match_code: group.twm_code,
            },
            { headers: { Authorization: `Bearer ${auth_token}` } },
          );

          saveResponses.push(data || {});
        }

        if (!saveResponses.length) {
          setSnack({
            open: true,
            type: "warning",
            msg: "No valid grouped records were available to save.",
          });
          return;
        }

        const lastSaved = saveResponses[saveResponses.length - 1];
        setSavedTwmCode(lastSaved?.twm_code || groups[0]?.twm_code || "");
        setSuccessDialogMessage(
          saveResponses.length === 1
            ? "Your grouped matching record was successfully saved."
            : `${saveResponses.length} grouped matching records were successfully saved.`,
        );
        await fetchPayPerUseData();
        resetUploadState({ abortUpload: false });
        setShowSuccessDialog(true);
        return;
      } catch (err) {
        setSnack({
          open: true,
          type: "error",
          msg:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Bulk save failed. Please try again.",
        });
        return;
      }
    }

    if (!selectedFiles || selectedFiles.length === 0) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please upload at least 3 documents including Invoice, GRN, and PO before saving. Payment Advice is optional.",
      });
      return;
    }

    if (!hasAllowedTwmFileCount(selectedFiles.length)) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please upload at least 3 documents including Invoice, GRN, and PO. Payment Advice is optional.",
      });
      return;
    }

    if (!selectedFiles.every(isDocumentUploadFile)) {
      setSnack({
        open: true,
        type: "error",
        msg: "Only supported document files are allowed.",
      });
      return;
    }

    const extractionValidation = getTwmExtractionValidation(response);
    if (!extractionValidation.isValid) {
      setSnack({
        open: true,
        type: "warning",
        msg: `Cannot save. ${getTwmExtractionValidationMessage(extractionValidation)}`,
      });
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("file", file));
    formData.append("user_token", user_token);
    formData.append("match_code", nextTwmCode || "");
    formData.append(
      "result_json",
      typeof response === "string" ? response : JSON.stringify(response || {}),
    );

    try {
      const request = await axios({
        url: `${API_ENDPOINT.save_three_way_matching}`,
        data: formData,
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSavedTwmCode(nextTwmCode);
      setSuccessDialogMessage(
        "Your Accounts Payable Automation record was successfully saved.",
      );
      await refetch();
      await fetchDashboardSummary();
      setSelectedFiles([]);
      setResponse(null);
      setUploadStatus("idle");
      setOverallProgress(0);
      setShowSuccessDialog(true);
    } catch (err) {
      setSnack({
        open: true,
        type: "error",
        msg: err?.response?.data?.error || "Save failed. Please try again.",
      });
    }
  };
  const handleSaveMatchingWithLoader = async () => {
    setSaving(true);
    try {
      await handleSaveMatching();
    } finally {
      setSaving(false);
    }
  };

  // const fetchMatchedData = async () => {
  //   try {
  //     const res = await axios.post(
  //       `${API_ENDPOINT.get_three_way_matching}`,
  //       { user_token },
  //       { headers: { Authorization: `Bearer ${auth_token}` } }
  //     );

  //     const twmList = res?.data || [];
  //     setMatchedResponse(twmList);

  //     // Set latest one
  //     const latest = twmList?.[0]?.result_json || null;
  //     setMatchedData(latest);

  //     const enrichedWithApprovers = await Promise.all(
  //       twmList.map(async (twm) => {
  //         try {
  //           const approverRes = await axios.post(
  //             `${API_ENDPOINT.get_twm_approver_names}`,
  //             { twm_token: twm.token },
  //             { headers: { Authorization: `Bearer ${auth_token}` } }
  //           );

  //           return {
  //             ...twm,
  //             approvers: approverRes.data?.approvers || [],
  //           };
  //         } catch (err) {
  //           console.error("Failed to fetch approvers for TWM:", twm.token);
  //           return {
  //             ...twm,
  //             approvers: [],
  //           };
  //         }
  //       })
  //     );

  //     // Optional: set this enriched list to another state
  //     setMatchedResponse(enrichedWithApprovers);
  //   } catch (error) {
  //     console.error("Error fetching TWM data", error);
  //   }
  // };

  // combine api

  // const fetchMatchedData = async () => {
  //   try {
  //     const res = await axios.post(
  //       `${API_ENDPOINT.get_three_way_matching_with_approvers}`,
  //       { user_token },
  //       { headers: { Authorization: `Bearer ${auth_token}` } }
  //     );

  //     const twmList = res?.data || [];
  //     setMatchedResponse(twmList);

  //     // Set latest one
  //     const latest = twmList?.[0]?.result_json || null;
  //     setMatchedData(latest);

  //   } catch (error) {
  //     console.error("Error fetching TWM data", error);
  //   }
  // };
  // useEffect(() => {
  //   fetchMatchedData();
  // }, []);

  //twm offline and online

  const {
    twmList,
    loading,
    approversLoading,
    approversError,
    paginationData: twmPaginationData,
    refetch,
  } = useOfflineTWM(
    isPayPerUse ? null : auth_token,
    isPayPerUse ? null : user_token,
    pageNumber,
    isPayPerUse ? "" : apiSearchTerm,
    apiStatusFilter,
  );

  const fetchPayPerUseData = useCallback(async () => {
    if (!isPayPerUse || !auth_token || !user_token) return;

    const requestId = payPerUseListRequestRef.current + 1;
    payPerUseListRequestRef.current = requestId;
    setPayPerUseListLoading(true);

    try {
      const { data } = await axios.post(
        API_ENDPOINT.get_bulk_three_way_matching,
        {
          user_token,
          search: apiSearchTerm,
          page: pageNumber,
          status: apiStatusFilter,
        },
        { headers: { Authorization: `Bearer ${auth_token}` } },
      );

      const list = Array.isArray(data?.results)
        ? data.results.map((record) => normalizeTwmRecord(record))
        : [];
      setMatchedResponse(list);
      setMatchedData(list?.[0]?.result_json || null);
      setPayPerUsePaginationData({
        current_page: data?.current_page || 1,
        current_page_count: data?.current_page_count || list.length || 0,
        page_count: data?.page_count || 0,
        total_count: data?.total_count || 0,
      });
    } catch (error) {
      console.error("Error fetching pay per use records", error);
      setMatchedResponse([]);
      setMatchedData(null);
      setPayPerUsePaginationData(null);
    } finally {
      if (payPerUseListRequestRef.current === requestId) {
        setPayPerUseListLoading(false);
      }
    }
  }, [
    apiSearchTerm,
    apiStatusFilter,
    auth_token,
    isPayPerUse,
    pageNumber,
    user_token,
  ]);

  const fetchPayPerUseResults = useCallback(
    async (batchToken) => {
      if (!isPayPerUse || !batchToken) return null;

      try {
        const { data } = await axios.post(
          API_ENDPOINT.get_bulk_twm_results,
          {
            user_token,
            batch_token: batchToken,
          },
          { headers: { Authorization: `Bearer ${auth_token}` } },
        );

        setPayPerUseResults(data || null);
        setPayPerUseStatus(data || null);
        setUploadStatus("success");
        setOverallProgress(Math.round(data?.progress_percentage || 100));
        payPerUseResultsFetchRef.current = true;
        return data;
      } catch (error) {
        console.error("Error fetching bulk TWM results", error);
        setUploadStatus("error");
        setSnack({
          open: true,
          type: "error",
          msg:
            error?.response?.data?.error ||
            "Unable to fetch extraction results. Please try again.",
        });
        return null;
      }
    },
    [auth_token, isPayPerUse, user_token],
  );

  const fetchPayPerUseStatus = useCallback(
    async (batchToken) => {
      if (!isPayPerUse || !batchToken) return null;

      try {
        const { data } = await axios.post(
          API_ENDPOINT.get_bulk_twm_status,
          {
            user_token,
            batch_token: batchToken,
          },
          { headers: { Authorization: `Bearer ${auth_token}` } },
        );

        const statusPayload = data || {};
        const normalizedStatus = String(
          statusPayload?.status || "",
        ).toUpperCase();

        setPayPerUseStatus(statusPayload);
        setOverallProgress(
          Math.max(
            0,
            Math.min(100, Math.round(statusPayload?.progress_percentage || 0)),
          ),
        );

        if (PAY_PER_USE_TERMINAL_STATUSES.has(normalizedStatus)) {
          if (payPerUsePollRef.current) {
            clearInterval(payPerUsePollRef.current);
            payPerUsePollRef.current = null;
          }

          if (PAY_PER_USE_SUCCESS_TERMINAL_STATUSES.has(normalizedStatus)) {
            setUploadStatus("processing");

            if (!payPerUseResultsFetchRef.current) {
              payPerUseResultsFetchRef.current = true;
              const results = await fetchPayPerUseResults(batchToken);

              if (results) {
                await fetchPayPerUseData();
              } else {
                payPerUseResultsFetchRef.current = false;
              }
            }
          } else {
            setUploadStatus("error");
            setSnack({
              open: true,
              type: "error",
              msg: statusPayload?.error || "Bulk extraction failed.",
            });
          }
        }

        return statusPayload;
      } catch (error) {
        console.error("Error fetching bulk TWM status", error);
        setUploadStatus("error");
        setSnack({
          open: true,
          type: "error",
          msg:
            error?.response?.data?.error ||
            "Unable to fetch extraction status. Please try again.",
        });
        return null;
      }
    },
    [
      auth_token,
      fetchPayPerUseData,
      fetchPayPerUseResults,
      isPayPerUse,
      user_token,
    ],
  );

  const activePaginationData = isPayPerUse
    ? payPerUsePaginationData
    : twmPaginationData;
  const tableApiLoading = isPayPerUse ? payPerUseListLoading : loading;
  const tableLoading = tableApiLoading || tableTransitionLoading;
  const tableLoadingMessage = tableApiLoading
    ? "Loading matching records..."
    : "Updating table...";

  const summaryRequestRef = useRef(0);
  const summarySearch = isPayPerUse ? apiSearchTerm : "";
  const summaryStatus = isPayPerUse ? apiStatusFilter : "";
  const summaryVendor = isPayPerUse ? selectedSuppliers.join(",") : "";

  const fetchDashboardSummary = useCallback(async () => {
    const requestId = ++summaryRequestRef.current;
    if (!auth_token || !user_token) return;

    setSummaryLoading(true);
    try {
      const endpoint = isPayPerUse
        ? API_ENDPOINT.get_bulk_twm_summary
        : API_ENDPOINT.get_three_way_matching_summary;
      const payload = isPayPerUse
        ? {
            user_token,
            search: summarySearch,
            status: summaryStatus,
            ...(summaryVendor
              ? { vendor: summaryVendor }
              : {}),
          }
        : {
            user_token,
          };

      const { data } = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${auth_token}` },
      });

      if (requestId !== summaryRequestRef.current) return;
      setSummaryResponse(normalizeMatchingSummaryResponse(data));
    } catch (error) {
      console.error("Error fetching TWM dashboard summary", error);

    } finally {
      if (requestId === summaryRequestRef.current) setSummaryLoading(false);
    }
  }, [
    summarySearch,
    summaryStatus,
    auth_token,
    isPayPerUse,
    summaryVendor,
    user_token,
  ]);

  useEffect(() => {
    setSummaryResponse(null);
    fetchDashboardSummary();
    return () => { summaryRequestRef.current += 1; };
  }, [fetchDashboardSummary]);

  useEffect(() => {
    if (!isPayPerUse) return;
    fetchPayPerUseData();
  }, [fetchPayPerUseData, isPayPerUse]);

  useEffect(() => {
    if (!isPayPerUse || !payPerUseBatchToken) return;

    const currentStatus = String(payPerUseStatus?.status || "").toUpperCase();
    if (PAY_PER_USE_TERMINAL_STATUSES.has(currentStatus)) {
      return;
    }

    fetchPayPerUseStatus(payPerUseBatchToken);

    if (payPerUsePollRef.current) {
      clearInterval(payPerUsePollRef.current);
    }

    payPerUsePollRef.current = setInterval(() => {
      fetchPayPerUseStatus(payPerUseBatchToken);
    }, PAY_PER_USE_POLL_INTERVAL);

    return () => {
      if (payPerUsePollRef.current) {
        clearInterval(payPerUsePollRef.current);
        payPerUsePollRef.current = null;
      }
    };
  }, [
    fetchPayPerUseStatus,
    isPayPerUse,
    payPerUseBatchToken,
    payPerUseStatus?.status,
  ]);

  useEffect(() => {
    if (!isPayPerUse || !payPerUseBatchToken) return;

    const currentStatus = String(payPerUseStatus?.status || "").toUpperCase();
    if (!PAY_PER_USE_SUCCESS_TERMINAL_STATUSES.has(currentStatus)) return;
    if (payPerUseResultsFetchRef.current) return;

    let active = true;
    payPerUseResultsFetchRef.current = true;

    const loadCompletedResults = async () => {
      const results = await fetchPayPerUseResults(payPerUseBatchToken);
      if (!active) return;

      if (results) {
        await fetchPayPerUseData();
      } else {
        payPerUseResultsFetchRef.current = false;
      }
    };

    loadCompletedResults();

    return () => {
      active = false;
    };
  }, [
    fetchPayPerUseData,
    fetchPayPerUseResults,
    isPayPerUse,
    payPerUseBatchToken,
    payPerUseStatus?.status,
  ]);

  useEffect(() => {
    if (isPayPerUse) return;
    const handleOnline = async () => {
      console.log("Network back online -> syncing TWM...");
      await refetch();
      await fetchDashboardSummary();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [fetchDashboardSummary, isPayPerUse, refetch]);

  useEffect(() => {
    if (isPayPerUse) return;
    if (loading) return;
    if (!twmList.length) {
      setMatchedResponse([]);
      setMatchedData(null);
      return;
    }

    const sortedByLatest = [...twmList]
      .map((record) => normalizeTwmRecord(record))
      .sort(
      (a, b) =>
        new Date(b?.created_at || 0).getTime() -
        new Date(a?.created_at || 0).getTime(),
    );

    setMatchedResponse(sortedByLatest);
    setMatchedData(sortedByLatest[0]?.result_json || null);
  }, [isPayPerUse, loading, twmList]);

  useEffect(() => {
    const chatActionRaw = sessionStorage.getItem("CHAT_ACTION");
    if (!chatActionRaw) return;

    const { action, three_way_search } = JSON.parse(chatActionRaw);
    console.log(three_way_search);

    if (action === "export_data") {
      if (!matchedresponse || matchedresponse.length === 0) {
        // wait until data is loaded
        return;
      }

      sessionStorage.removeItem("CHAT_ACTION");
      downloadAllXLSX();
    }

    if (action === "search_records") {
      setPageNumber(1);
      setSelectedSupplier("");
      setSearchTerm(three_way_search);
    }

    if (action === "create_twm") {
      if (!isPayPerUse) {
        fetchNextCode();
      }
      go("upload");
    }

    if (action === "open_twm_specific_record") {
      console.log("hii");
      if (!matchedresponse || matchedresponse.length === 0) return;

      const safeCode = three_way_search?.toString().trim().toLowerCase();

      console.log(safeCode, "safe");

      const matchedCard = matchedresponse.find(
        (item) => item.match_code?.toLowerCase() === safeCode,
      );

      console.log(matchedCard);
      if (!matchedCard) {
        console.warn("P2P record not found:", safeCode);
        return;
      }

      handleViewItems(matchedCard);
      sessionStorage.removeItem("CHAT_ACTION");
    }
  }, [isPayPerUse, matchedresponse]);

  useEffect(() => {
    if (mode !== "upload" || uploadStatus === "uploading") return;

    const pendingUpload = consumePendingChatbotUpload("create_twm");
    if (!pendingUpload?.files?.length) return;

    const pendingFiles = Array.from(pendingUpload.files || []);
    const pendingDocumentFiles = pendingFiles.filter(isDocumentUploadFile);
    if (
      (!isPayPerUse &&
        (!hasAllowedTwmFileCount(pendingFiles.length) ||
          !hasAllowedTwmFileCount(pendingDocumentFiles.length))) ||
      (isPayPerUse && pendingDocumentFiles.length === 0)
    ) {
      setSnack({
        open: true,
        type: "warning",
        msg: isPayPerUse
          ? "Please select at least one document."
          : "Please select at least 3 documents including Invoice, GRN, and PO. Payment Advice is optional.",
      });
      return;
    }

    if (getDuplicateFileNames(pendingDocumentFiles).length > 0) {
      setSnack({
        open: true,
        type: "error",
        msg: "Same file name is not allowed. Please rename the documents and upload again.",
      });
      return;
    }

    setSelectedFiles(pendingDocumentFiles);
    setResponse(null);
    setUploadStatus("idle");
    setOverallProgress(0);
    setPayPerUseStatus(null);
    setPayPerUseResults(null);
    pendingChatAutoUploadRef.current = true;
  }, [isPayPerUse, mode, uploadStatus]);

  useEffect(() => {
    if (!pendingChatAutoUploadRef.current) return;
    if (mode !== "upload") return;
    if (!selectedFiles.length || uploadStatus === "uploading") return;

    pendingChatAutoUploadRef.current = false;
    handleUploadRef.current?.();
  }, [mode, selectedFiles, uploadStatus]);

  const buildViewDataFromMatches = (matchesObj) => {
    if (!matchesObj || typeof matchesObj !== "object") return null;
    return {
      poData: Array.isArray(matchesObj.po_data) ? matchesObj.po_data : [],
      invoiceData: Array.isArray(matchesObj.invoice_data)
        ? matchesObj.invoice_data
        : [],
      grnData: Array.isArray(matchesObj.grn_data) ? matchesObj.grn_data : [],
      paymentAdviceData: Array.isArray(matchesObj.payment_advice_data)
        ? matchesObj.payment_advice_data
        : [],
    };
  };

  const toggleReject = (id) => {
    // setOpenMap((m) => ({ ...m, [id]: !m[id] }));
  };

  const closeReject = (id) => {
    // setOpenMap((m) => ({ ...m, [id]: false }));
  };

  const approveAndRejectDoc = async (action, twm_token) => {
    try {
      if (!navigator.onLine) {
        await twmRepository.saveOfflineAction(action, twm_token, user_token);

        setOpenOfflinePopup(true);
      } else {
        const formData = new FormData();
        formData.append("action", action);
        formData.append("user_token", user_token);
        formData.append("twm_token", twm_token);

        await axios.post(API_ENDPOINT.approve_reject_twm, formData, {
          headers: { Authorization: `Bearer ${auth_token}` },
        });
      }

      if (action === "accept") {
        setApprovedTokens((prev) => [...prev, twm_token]);
      }

      await refetch();
      await fetchDashboardSummary();
    } catch (err) {
      console.error(err);
    }
  };

  const buildRowsFromResponse = (dataObj = {}) => {
    const rows = [];
    const pick = (obj, keys) => {
      if (!obj) return "";
      for (const k of keys) {
        const v = obj[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
      }
      return "";
    };
    const pickNested = (obj, paths) => {
      if (!obj) return "";
      for (const path of paths) {
        let current = obj;
        let found = true;
        for (const key of path) {
          if (current === undefined || current === null) {
            found = false;
            break;
          }
          current = current[key];
        }
        if (
          found &&
          current !== undefined &&
          current !== null &&
          String(current).trim() !== ""
        ) {
          return current;
        }
      }
      return "";
    };

    // ---- PO ----
    const poList = Array.isArray(dataObj.po_data) ? dataObj.po_data : [];
    poList.forEach((po) => {
      const items = Array.isArray(po.line_items) ? po.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "PO",
          doc_no:
            po.purchase_order_no ||
            po.purchase_order_number ||
            po.po_number ||
            po.po_no ||
            po.order_number ||
            "",
          doc_date: po.order_date || po.po_date || "",
          vendor: po.vendor || po.vendor_name || po.supplier_name || "",
          company_business_unit: pick(po, [
            "company",
            "business_unit",
            "businessUnit",
            "org_unit",
            "org",
          ]),
          location_plant: pick(po, ["location", "plant", "delivery_location"]),
          hsn: li.hsn_code || li.hsn || "",
          part_no:
            li.part_number ||
            li.part_no ||
            li.item_code ||
            li.cat_code ||
            li.code ||
            "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li["qty."] != null
              ? li["qty."]
              : li.qty != null
                ? li.qty
                : li.quantity_received != null
                  ? li.quantity_received
                  : li.received_quantity != null
                    ? li.received_quantity
                    : li.received_q != null
                      ? li.received_q
                      : "",
          uom:
            li.uom ||
            pickNested(li, [["extra_fields", "extra_fields", "uom"]]) ||
            "",
          unit_price:
            li.rate != null
              ? li.rate
              : li.unit_price != null
                ? li.unit_price
                : li.parsed_rate != null
                  ? li.parsed_rate
                  : "",
          tax_percent: pick(li, [
            "tax_percent",
            "gst_percent",
            "tax_percentage",
            "tax_rate",
          ]),
          tax_type: pick(li, ["tax_type", "gst_type", "tax_category"]),
          tax_amount: pick(li, ["tax_amount", "gst_amount"]),
          amount:
            li.line_amount != null
              ? li.line_amount
              : li.amount != null
                ? li.amount
                : li.taxable_amount != null
                  ? li.taxable_amount
                  : li.line_total != null
                    ? li.line_total
                    : li.parsed_amount != null
                      ? li.parsed_amount
                      : "",
          variance_tax_other: pick(li, [
            "variance_tax_other",
            "tax_variance",
            "other_variance",
          ]),
          remarks: pick(li, ["remarks", "remark", "exception_notes", "notes"]),
          attachments: pick(po, [
            "attachments",
            "attachment",
            "attachment_url",
            "document_url",
          ]),
        });
      });
    });

    // ---- GRN ----
    const grnList = Array.isArray(dataObj.grn_data) ? dataObj.grn_data : [];
    grnList.forEach((grn) => {
      const items = Array.isArray(grn.line_items) ? grn.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "GRN",
          doc_no:
            grn.order_number ||
            grn.grn_number ||
            grn.grn_no ||
            grn.goods_receipt_no ||
            grn.receipt_number ||
            "",
          doc_date: grn.date || grn.grn_date || grn.received_date || "",
          vendor: grn.vendor_name || grn.vendor || "",
          company_business_unit: pick(grn, [
            "company",
            "business_unit",
            "businessUnit",
            "org_unit",
            "org",
          ]),
          location_plant: pick(grn, ["location", "plant", "delivery_location"]),
          hsn: li.hsn_code || li.hsn || "",
          part_no:
            li.part_number ||
            li.part_no ||
            li.item_code ||
            li.cat_code ||
            li.code ||
            "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li.quantity_received != null
              ? li.quantity_received
              : li.received_quantity != null
                ? li.received_quantity
                : li.received_q != null
                  ? li.received_q
                  : li["qty."] != null
                    ? li["qty."]
                    : li.qty != null
                      ? li.qty
                      : "",
          uom:
            li.uom ||
            pickNested(li, [["extra_fields", "extra_fields", "uom"]]) ||
            "",
          unit_price:
            li.rate != null
              ? li.rate
              : li.unit_price != null
                ? li.unit_price
                : li.parsed_rate != null
                  ? li.parsed_rate
                  : "",
          tax_percent: pick(li, [
            "tax_percent",
            "gst_percent",
            "tax_percentage",
            "tax_rate",
          ]),
          tax_type: pick(li, ["tax_type", "gst_type", "tax_category"]),
          tax_amount: pick(li, ["tax_amount", "gst_amount"]),
          amount:
            li.line_amount != null
              ? li.line_amount
              : li.amount != null
                ? li.amount
                : li.taxable_amount != null
                  ? li.taxable_amount
                  : li.line_total != null
                    ? li.line_total
                    : li.parsed_amount != null
                      ? li.parsed_amount
                      : "",
          lot_number: li.lot_number || "",
          expiry_date: li.expiry_date || "",
          variance_tax_other: pick(li, [
            "variance_tax_other",
            "tax_variance",
            "other_variance",
          ]),
          remarks: pick(li, ["remarks", "remark", "exception_notes", "notes"]),
          attachments: pick(grn, [
            "attachments",
            "attachment",
            "attachment_url",
            "document_url",
          ]),
        });
      });
    });

    // ---- INVOICE ----
    const invList = Array.isArray(dataObj.invoice_data)
      ? dataObj.invoice_data
      : [];
    invList.forEach((inv) => {
      const items = Array.isArray(inv.line_items) ? inv.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "INVOICE",
          doc_no: inv.invoice_number || "",
          doc_date: inv.invoice_date || "",
          vendor: inv.vendor_name || inv.supplier_name || "",
          company_business_unit: pick(inv, [
            "company",
            "business_unit",
            "businessUnit",
            "org_unit",
            "org",
          ]),
          location_plant: pick(inv, ["location", "plant", "delivery_location"]),
          hsn: li.hsn_code || li.hsn || "",
          part_no:
            li.part_number ||
            li.part_no ||
            li.item_code ||
            li.cat_code ||
            li.code ||
            "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li.qty != null
              ? li.qty
              : li["qty."] != null
                ? li["qty."]
                : li.quantity_received != null
                  ? li.quantity_received
                  : li.received_quantity != null
                    ? li.received_quantity
                    : li.received_q != null
                      ? li.received_q
                      : "",
          uom:
            li.uom ||
            pickNested(li, [["extra_fields", "extra_fields", "uom"]]) ||
            "",
          unit_price:
            li.unit_price != null
              ? li.unit_price
              : li.rate != null
                ? li.rate
                : li.parsed_rate != null
                  ? li.parsed_rate
                  : "",
          tax_percent: pick(li, [
            "tax_percent",
            "gst_percent",
            "tax_percentage",
            "tax_rate",
          ]),
          tax_type: pick(li, ["tax_type", "gst_type", "tax_category"]),
          tax_amount: pick(li, ["tax_amount", "gst_amount"]),
          amount:
            li.line_amount != null
              ? li.line_amount
              : li.amount != null
                ? li.amount
                : li.taxable_amount != null
                  ? li.taxable_amount
                  : li.line_total != null
                    ? li.line_total
                    : li.parsed_amount != null
                      ? li.parsed_amount
                      : "",
          po_ref:
            inv.purchase_order_number ||
            inv.po_number ||
            inv.po_no ||
            "",
          variance_tax_other: pick(li, [
            "variance_tax_other",
            "tax_variance",
            "other_variance",
          ]),
          remarks: pick(li, ["remarks", "remark", "exception_notes", "notes"]),
          attachments: pick(inv, [
            "attachments",
            "attachment",
            "attachment_url",
            "document_url",
          ]),
        });
      });
    });

    return rows;
  };

  const toCSV = (dataObj = {}) => {
    const rows = buildRowsFromResponse(dataObj);
    if (rows.length === 0) return "source\n"; // empty CSV with header

    // Preferred header order, plus any extras we discover
    const preferred = [
      "source",
      "doc_no",
      "doc_date",
      "po_ref",
      "vendor",
      "part_no",
      "description",
      "hsn",
      "qty",
      "uom",
      "unit_price",
      "amount",
      "lot_number",
      "expiry_date",
    ];

    // Build union of keys actually present
    const found = new Set();
    rows.forEach((r) => Object.keys(r).forEach((k) => found.add(k)));

    // Final headers = preferred (if present) + any remaining keys
    const headers = [
      ...preferred.filter((k) => found.has(k)),
      ...[...found].filter((k) => !preferred.includes(k)),
    ];

    // CSV escape
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      // NOTE: we intentionally do not strip commas; we escape them correctly.
    };

    const lines = [
      headers.join(","),
      ...rows.map((row) => headers.map((h) => esc(row[h])).join(",")),
    ];

    return lines.join("\n");
  };

  const downloadXLSX = (card) => {
    try {
      const normalizedCard = normalizeTwmRecord(card);
      const rowCount = downloadMatchingWorkbook(
        [normalizedCard],
        `${normalizedCard.match_code || normalizedCard.twm_code || "twm"}-data.xlsx`,
      );
      if (!rowCount) {
        setSnack({ open: true, type: "warning", msg: "No data to export." });
      }
    } catch (e) {
      setSnack({ open: true, type: "error", msg: "Failed to download XLSX." });
    }
  };

  const downloadAllXLSX = (records = filterRecords) => {
    try {
      if (!records || records.length === 0) {
        setSnack({ open: true, type: "warning", msg: "No data to export." });
        return;
      }

      const rowCount = downloadMatchingWorkbook(
        records,
        "three_way_matching_report.xlsx",
      );
      if (!rowCount) {
        setSnack({ open: true, type: "warning", msg: "No data to export." });
        return;
      }
    } catch (err) {
      console.error(err);
      setSnack({ open: true, type: "error", msg: "Failed to export data." });
    }
  };

  function downloadDashboardReportCSV() {
    try {
      if (!matchedresponse || matchedresponse.length === 0) {
        setSnack({ open: true, type: "warning", msg: "No data to export." });
        return;
      }

      const rows = matchedresponse.map((card) => {
        const normalizedCard = normalizeTwmRecord(card);
        const rj = normalizedCard?.result_json || {};
        const poData = rj?.po_data || [];
        const grnData = rj?.grn_data || [];
        const invoiceData = rj?.invoice_data || [];

        const po = poData?.[0] || {};
        const grn = grnData?.[0] || {};
        const invoice = invoiceData?.[0] || {};

        const recordStatuses = getPayPerUseRecordStatuses(normalizedCard);
        const quantityMatchStatus = recordStatuses.quantity;
        const priceMatchStatus = recordStatuses.price;
        const itemMatchStatus = recordStatuses.item;
        const overallMatchStatus = getPayPerUseOverallMatchStatus(normalizedCard);
        const matchingDetails = getPayPerUseMatchingDetails(normalizedCard);
        const statusSummary = matchingDetails?.status_summary || {};
        const paymentAdviceValues = getPaymentAdviceExportValues(
          normalizedCard,
          rj,
          matchingDetails,
        );
        const hasRejected = overallMatchStatus.type === "rejected";

        return {
          match_code: normalizedCard.match_code || normalizedCard.twm_code || "",
          matching_type: formatMatchingMode(normalizedCard.matching_mode),
          matching_status: overallMatchStatus.status,
          po_status:
            getExportValue(statusSummary, ["po_status", "purchase_order_status"]) ||
            getExportValue(po, ["status", "po_status", "approval_status"]) ||
            getExportValue(normalizedCard, ["po_status", "purchase_order_status"]) ||
            overallMatchStatus.status,
          invoice_status:
            getExportValue(statusSummary, ["invoice_status", "bill_status"]) ||
            getExportValue(invoice, ["status", "invoice_status", "approval_status"]) ||
            getExportValue(normalizedCard, ["invoice_status", "bill_status"]) ||
            overallMatchStatus.status,
          grn_status:
            getExportValue(statusSummary, ["grn_status", "mrn_status"]) ||
            getExportValue(grn, ["status", "grn_status", "approval_status"]) ||
            getExportValue(normalizedCard, ["grn_status", "mrn_status"]) ||
            overallMatchStatus.status,
          ...paymentAdviceValues,
          exception_flag: hasRejected ? "Yes" : "No",
          vendor_name:
            po?.vendor ||
            po?.vendor_name ||
            po?.supplier_name ||
            invoice?.vendor_name ||
            invoice?.supplier_name ||
            grn?.vendor_name ||
            grn?.vendor ||
            "",
          company_business_unit:
            po?.business_unit ||
            po?.company ||
            grn?.business_unit ||
            grn?.company ||
            invoice?.business_unit ||
            invoice?.company ||
            "",
          location_plant:
            po?.location ||
            po?.plant ||
            grn?.location ||
            grn?.plant ||
            invoice?.location ||
            invoice?.plant ||
            "",
          po_number: inferPayPerUsePoNumber(card),
          po_bill_date: po?.order_date || "",
          invoice_number: inferPayPerUseInvoiceNumber(normalizedCard),
          invoice_date: invoice?.invoice_date || "",
          grn_number: inferPayPerUseGrnNumber(card),
          grn_bill_date: grn?.grn_bill_date || grn?.bill_date || "",
          po_grand_total:
            po?.total_tax_included ||
            po?.tax_included_total ||
            po?.total_with_tax ||
            po?.grand_total ||
            po?.total_amount ||
            po?.amount ||
            "",
          invoice_grand_total: inferPayPerUseInvoiceTotal(normalizedCard),
          quantity_status: quantityMatchStatus.status,
          price_status: priceMatchStatus.status,
          item_status: itemMatchStatus.status,
          matching_date: normalizedCard?.created_at || "",
          approval_status:
            normalizedCard?.approval_status ||
            normalizedCard?.your_status ||
            normalizedCard?.status ||
            "",
        };
      });

      const preferredHeaders = [
        "match_code",
        "matching_type",
        "matching_status",
        "po_status",
        "invoice_status",
        "grn_status",
        "payment_advice_status",
        "payment_advice_amount",
        "payment_advice_mode",
        "payment_advice_reference",
        "payment_advice_count",
        "payment_advice_document_count",
        "payment_outstanding_amount",
        "payment_coverage_percent",
        "exception_flag",
        "vendor_name",
        "company_business_unit",
        "location_plant",
        "po_number",
        "po_bill_date",
        "invoice_number",
        "invoice_date",
        "grn_number",
        "grn_bill_date",
        "po_grand_total",
        "invoice_grand_total",
        "quantity_status",
        "price_status",
        "item_status",
        "matching_date",
        "approval_status",
      ];

      const found = new Set();
      rows.forEach((r) => Object.keys(r || {}).forEach((k) => found.add(k)));
      const headers = [
        ...preferredHeaders.filter((h) => found.has(h)),
        ...[...found].filter((k) => !preferredHeaders.includes(k)),
      ];

      const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const csv = [
        headers.join(","),
        ...rows.map((row) => headers.map((h) => esc(row[h])).join(",")),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "three_way_matching_dashboard_report.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        type: "error",
        msg: "Failed to export dashboard report.",
      });
    }
  }

  const handleViewItems = (card) => {
    const normalizedCard = normalizeTwmRecord(card);
    setSelectedCard({
      ...(normalizedCard.result_json || {}),
      match_code: normalizedCard.match_code || normalizedCard.twm_code,
      matching_mode: normalizedCard.matching_mode,
      status: normalizedCard.status,
      approval_status: normalizedCard.approval_status,
      created_at: normalizedCard.created_at,
      matching_details: getPayPerUseMatchingDetails(normalizedCard),
    });
    setViewItemsDialog(true);
  };

  const handleOpenPayPerUsePreview = (group) => {
    setSelectedPayPerUseGroup(group || null);
  };

  const handleClosePayPerUsePreview = () => {
    setSelectedPayPerUseGroup(null);
  };

  // Helper function to check if current user is an approver and can take action
  const canUserApproveReject = (card) => {
    if (!card.approvers || !user_id) return false;

    // Convert approvers object to array if needed
    const approversArray = Array.isArray(card.approvers)
      ? card.approvers
      : card.approvers
        ? Object.entries(card.approvers).map(([id, status]) => ({
            id,
            status,
            name: id,
          }))
        : [];

    // Find current user in approvers
    const currentUserApprover = approversArray.find(
      (approver) =>
        approver.id === user_id.toString() || approver.id === user_id,
    );

    // User is an approver and hasn't taken action yet (status is PENDING or doesn't exist)
    return currentUserApprover && currentUserApprover.status === "PENDING";
  };

  // Helper function to get current user's approval status
  const getUserApprovalStatus = (card) => {
    if (!card.approvers || !user_id) return null;

    const approversArray = Array.isArray(card.approvers)
      ? card.approvers
      : card.approvers
        ? Object.entries(card.approvers).map(([id, status]) => ({
            id,
            status,
            name: id,
          }))
        : [];

    const currentUserApprover = approversArray.find(
      (approver) =>
        approver.id === user_id.toString() || approver.id === user_id,
    );

    return currentUserApprover ? currentUserApprover.status : null;
  };

  // Helper function to check if current user is an approver for any card
  const isUserAnApprover = () => {
    if (!matchedresponse || !user_id) return false;

    return matchedresponse.some((card) => {
      if (!card.approvers) return false;

      const approversArray = Array.isArray(card.approvers)
        ? card.approvers
        : card.approvers
          ? Object.entries(card.approvers).map(([id, status]) => ({
              id,
              status,
              name: id,
            }))
          : [];

      return approversArray.some(
        (approver) =>
          approver.id === user_id.toString() || approver.id === user_id,
      );
    });
  };

  const userIsApprover = isUserAnApprover();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <>
      <Box>
        {/* MAIN PAGE: search, cards, button (no upload logic/search on upload) */}

        {/* <Box sx={{ width: "100%", mb: 1.5, ml:2.5}}>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      color="primary"
                      gutterBottom
                      sx={{
                        fontFamily: "GilroyBold",
                        fontSize: isMobile ? "16px" : "20px",
                        lineHeight: 1.3,
                      }}
                    >
                      Accounts Payable Automation Summary
                    </Typography>
                    <Typography
                      sx={{
                        color: "#64748b",
                        fontSize: "14px",
                        fontFamily:"gilroyMedium",
                        lineHeight: 1.5,
                      }}
                    >
                      Review all matched purchase orders, invoices, and GRNs.
                    </Typography>
                  </Box> */}
        {mode === "main" && (
          <>
            {/* Top bar with search + button */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "minmax(320px, 2fr) minmax(220px, 1fr) minmax(160px, 0.75fr) minmax(150px, auto) minmax(170px, auto)",
                },
                alignItems: "center",
                gap: { xs: 1.25, md: 1.5 },
                p: { xs: 1.25, sm: 2 },
                width: "100%",
                "& input": {
                  fontFamily: "gilroyMedium",
                  fontSize: "14px",
                },
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  width: "100%",
                  gridColumn: { xs: "auto", md: "1 / -1", lg: "auto" },
                }}
              >
                <SearchBar
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by Matching ID, Supplier name, PO ID, Invoice ID, GRN ID..."
                />
              </Box>
                <Select
                  multiple
                  value={selectedSuppliers}
                  onChange={(event) => handleSupplierChange(event.target.value)}
                  size="small"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected.length) return "Supplier: All";

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          maxWidth: "100%",
                          overflowX: "auto",
                          overflowY: "hidden",
                          "&::-webkit-scrollbar": { height: 0 },
                        }}
                      >
                        {selected.map((supplier) => (
                          <Chip
                            key={supplier}
                            label={supplier}
                            size="small"
                            deleteIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                            onDelete={(event) => {
                              event.stopPropagation();
                              removeSupplierFilter(supplier);
                            }}
                            sx={{
                              maxWidth: 150,
                              height: 26,
                              flexShrink: 0,
                              borderRadius: "14px",
                              backgroundColor: "#eff6ff",
                              color: "#1d4ed8",
                              border: "1px solid #bfdbfe",
                              fontFamily: "gilroyMedium",
                              fontSize: "13px",
                              "& .MuiChip-label": {
                                px: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                              "& .MuiChip-deleteIcon": {
                                color: "#1d4ed8",
                                mr: 0.6,
                                "&:hover": {
                                  color: "#dc2626",
                                },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    );
                  }}
                  sx={{
                    minHeight: 42,
                    width: "100%",
                    minWidth: 0,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    fontFamily: "gilroyMedium",
                    fontSize: "13px",
                    "& .MuiSelect-select": {
                      py: 0.75,
                      pl: 1.25,
                      display: "flex",
                      alignItems: "center",
                      minHeight: "26px",
                    },
                  }}
                >
                  {supplierOptions.map((supplier) => (
                    <MenuItem
                      key={supplier}
                      value={supplier}
                      sx={{ fontSize: "13px", maxWidth: 320 }}
                    >
                      <ListItemText
                        primary={supplier}
                        primaryTypographyProps={{
                          fontSize: "13px",
                          fontFamily: "gilroyMedium",
                          noWrap: true,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={approvalStatusFilter}
                  onChange={(event) =>
                    setApprovalStatusFilter(event.target.value)
                  }
                  size="small"
                  displayEmpty
                  sx={{
                    height: 42,
                    width: "100%",
                    minWidth: 0,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    fontFamily: "gilroyMedium",
                    fontSize: "13px",
                    "& .MuiSelect-select": {
                      py: 1,
                      pl: 1.25,
                    },
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: "13px" }}>
                    Status: All
                  </MenuItem>
                  <MenuItem value="PENDING" sx={{ fontSize: "13px" }}>
                    Status: Pending
                  </MenuItem>
                  <MenuItem value="APPROVED" sx={{ fontSize: "13px" }}>
                    Status: Approved
                  </MenuItem>
                  <MenuItem value="REJECTED" sx={{ fontSize: "13px" }}>
                    Status: Rejected
                  </MenuItem>
                </Select>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadAllXLSX(filterRecords)}
                  sx={{
                    fontFamily: "gilroyMedium",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 1,
                    fontSize: "14px",
                    minHeight: "42px",
                    width: { xs: "100%", md: "100%", lg: "auto" },
                    whiteSpace: "nowrap",
                    justifySelf: { xs: "stretch", lg: "start" },
                    "& .MuiButton-startIcon": { flexShrink: 0 },
                  }}
                >
                  Export Report
                </Button>

                {![
                  "Finance Analyst",
                  "Invoice Analyst",
                  "Business Finance",
                  "Account Payable",
                ].includes(user_designation) && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={async () => {
                      if (!isPayPerUse) {
                        await fetchNextCode();
                      }
                      go("upload");
                    }}
                    sx={{
                      fontFamily: "gilroySemiBold",
                      // fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 1,
                      fontSize: "14px",
                      minHeight: "42px",
                      px: 2,
                      width: { xs: "100%", md: "100%", lg: "auto" },
                      whiteSpace: "nowrap",
                      justifySelf: { xs: "stretch", lg: "start" },
                      "& .MuiButton-startIcon": { flexShrink: 0 },
                    }}
                  >
                    {isPayPerUse ? "Bulk Upload & Match" : "Create Matching"}
                  </Button>
                )}
            </Box>

            {/* Cards Section */}
            {matchedresponse === null || (tableLoading && filterRecords.length === 0) ? (
              // still loading the list
              <Box sx={{ px: 2, py: 4 }}>
                <LinearProgress />
                <Typography
                  sx={{
                    mt: 1.5,
                    textAlign: "center",
                    fontFamily: "gilroyMedium",
                    fontSize: "14px",
                    color: "#64748b",
                  }}
                >
                  {tableLoadingMessage}
                </Typography>
              </Box>
            ) : filterRecords.length === 0 ? (
              // no records OR no records match the current search
              <EmptyState
                title="No records found"
                subtitle={
                  [
                    "Finance Analyst",
                    "Invoice Analyst",
                    "Business Finance",
                    "Account Payable",
                    "Buyer",
                  ].includes(user_designation)
                    ? "Try clearing the search."
                    : "Try clearing the search, or create your first Accounts Payable Automation record."
                }
              />
            ) : (
              <Grid container p={2}>
                <Grid item xs={12}>
                  <Box sx={{ width: "100%", mx: "auto" }}>
                    {/* <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadAllXLSX(filterRecords)}
                      sx={{
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        fontSize: "14px",
                        mb: 5,
                      }}
                    >
                      Download All
                    </Button>
                  </Box> */}

                    {/* Unified Paper for Table + Pagination */}
                    <Paper
                      elevation={2}
                      sx={{
                        fontFamily: "gilroyMedium",
                        borderRadius: "10px",
                        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden", // ensures seamless pagination alignment
                        position: "relative",
                      }}
                    >
                      {/* Table Container */}
                      <TableContainer
                        ref={scrollRef}
                        sx={{
                          maxHeight: "60vh",
                          overflowX: "auto",
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                          "&::-webkit-scrollbar": { height: 0, width: 0 },
                        }}
                      >
                        <Table
                          size="small"
                          stickyHeader
                          sx={{
                            minWidth: userIsApprover ? 2685 : 2535,
                            tableLayout: "fixed",
                            borderRight: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            "&& .MuiTableCell-root, && .MuiTypography-root, && .MuiChip-label, && .MuiButton-root, && .MuiTableSortLabel-root": {
                              color: "#000000",
                              fontSize: "14px",
                            },
                            "&& .MuiTableCell-root": {
                              padding: "13px 18px 11px",
                              lineHeight: "20px",
                            },
                            "&& .MuiTableRow-root": {
                              height: "60px",
                            },
                            "&& .MuiTableCell-head": {
                              backgroundColor: "#e0e9ee",
                              fontFamily: "GilroySemiBold",
                              fontWeight: 600,
                              borderBottom: "1px solid #d1dce3",
                            },
                            "&& .MuiTableCell-body": {
                              borderBottom: "1.3px solid var(--dsb-table-border-gray, #97979720)",
                              transition: "background-color 150ms ease",
                            },
                            "&& .MuiTableBody-root .MuiTableRow-root:hover": {
                              backgroundColor: "#97979712",
                            },
                            "&& .MuiTableBody-root .MuiTableRow-root:hover > .MuiTableCell-root": {
                              backgroundColor: "#f7f7f7",
                            },
                          }}
                        >
                          {/* ===== Table Head ===== */}
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: "#f8fafc",
                                "& th": {
                                  py: 1.2,
                                  borderBottom: "2px solid #e2e8f0",
                                  fontWeight: 700,
                                  fontFamily: "GilroyBold",
                                  fontSize: "14px",
                                  color: "#334155",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  whiteSpace: "nowrap",
                                  left: 0,
                                  zIndex: 4,
                                  width: listColumnWidths.matchCode,
                                  minWidth: listColumnWidths.matchCode,
                                  maxWidth: listColumnWidths.matchCode,
                                  backgroundColor: "#f8fafc",
                                  cursor: "pointer",
                                  boxShadow: "2px 0 0 #e2e8f0",
                                  "&:hover": { backgroundColor: "#f1f5f9" },
                                }}
                              >
                                {renderTableSortLabel("match_code", "Matching ID")}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.matchingType,
                                  minWidth: listColumnWidths.matchingType,
                                  maxWidth: listColumnWidths.matchingType,
                                }}
                              >
                                Matching Type
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.supplierName,
                                  minWidth: listColumnWidths.supplierName,
                                  maxWidth: listColumnWidths.supplierName,
                                }}
                              >
                                Supplier Name
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.poId,
                                  minWidth: listColumnWidths.poId,
                                  maxWidth: listColumnWidths.poId,
                                }}
                              >
                                PO ID
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.poDate,
                                  minWidth: listColumnWidths.poDate,
                                  maxWidth: listColumnWidths.poDate,
                                }}
                              >
                                PO Bill Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.invoiceNumber,
                                  minWidth: listColumnWidths.invoiceNumber,
                                  maxWidth: listColumnWidths.invoiceNumber,
                                }}
                              >
                                Invoice Number
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.invoiceDate,
                                  minWidth: listColumnWidths.invoiceDate,
                                  maxWidth: listColumnWidths.invoiceDate,
                                }}
                              >
                                Invoice Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.grnNumber,
                                  minWidth: listColumnWidths.grnNumber,
                                  maxWidth: listColumnWidths.grnNumber,
                                }}
                              >
                                GRN Number
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.grnDate,
                                  minWidth: listColumnWidths.grnDate,
                                  maxWidth: listColumnWidths.grnDate,
                                }}
                              >
                                GRN Bill Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.paymentRefNo,
                                  minWidth: listColumnWidths.paymentRefNo,
                                  maxWidth: listColumnWidths.paymentRefNo,
                                }}
                              >
                                Payment Ref No
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.advancePaid,
                                  minWidth: listColumnWidths.advancePaid,
                                  maxWidth: listColumnWidths.advancePaid,
                                }}
                              >
                                Advance Paid
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.balanceDue,
                                  minWidth: listColumnWidths.balanceDue,
                                  maxWidth: listColumnWidths.balanceDue,
                                }}
                              >
                                Balance Due
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.poTotal,
                                  minWidth: listColumnWidths.poTotal,
                                  maxWidth: listColumnWidths.poTotal,
                                }}
                              >
                                PO Grand Total
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.invoiceTotal,
                                  minWidth: listColumnWidths.invoiceTotal,
                                  maxWidth: listColumnWidths.invoiceTotal,
                                }}
                              >
                                Invoice Grand Total
                              </TableCell>

                              {/* New Status Columns */}
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.itemStatus,
                                  minWidth: listColumnWidths.itemStatus,
                                  maxWidth: listColumnWidths.itemStatus,
                                }}
                              >
                                Item Status
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.quantityStatus,
                                  minWidth: listColumnWidths.quantityStatus,
                                  maxWidth: listColumnWidths.quantityStatus,
                                }}
                              >
                                Quantity Status
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.priceStatus,
                                  minWidth: listColumnWidths.priceStatus,
                                  maxWidth: listColumnWidths.priceStatus,
                                }}
                              >
                                Price Status
                              </TableCell>

                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.matchingDate,
                                  minWidth: listColumnWidths.matchingDate,
                                  maxWidth: listColumnWidths.matchingDate,
                                }}
                              >
                                {renderTableSortLabel(
                                  "created_at",
                                  "Matching Date",
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.approvalStatus,
                                  minWidth: listColumnWidths.approvalStatus,
                                  maxWidth: listColumnWidths.approvalStatus,
                                }}
                              >
                                Approval Status
                              </TableCell>

                              {userIsApprover && (
                                <TableCell
                                  sx={{
                                    position: "sticky",
                                    right: listColumnWidths.action,
                                    width: listColumnWidths.approveReject,
                                    minWidth: listColumnWidths.approveReject,
                                    maxWidth: listColumnWidths.approveReject,
                                    zIndex: 3,
                                    backgroundColor: "#f8fafc",
                                    whiteSpace: "nowrap",
                                    borderLeft: "1px solid #e2e8f0",
                                    boxShadow:
                                      "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                                  }}
                                >
                                  Approve / Reject
                                </TableCell>
                              )}

                              <TableCell
                                sx={{
                                  position: "sticky",
                                  right: 0,
                                  width: listColumnWidths.action,
                                  minWidth: listColumnWidths.action,
                                  maxWidth: listColumnWidths.action,
                                  zIndex: 5,
                                  backgroundColor: "#f8fafc",
                                  textAlign: "center",
                                  borderLeft: "1px solid #e2e8f0",
                                  boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                                }}
                              >
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          {/* ===== Table Body ===== */}
                          <TableBody>
                            {getSortedData().map((card, idx) => {
                              const normalizedCard = normalizeTwmRecord(card);
                              const resultJson = normalizedCard.result_json || {};
                              const po = resultJson?.po_data?.[0];
                              const invoice =
                                resultJson?.invoice_data?.[0];
                              const grn = resultJson?.grn_data?.[0];
                              const paymentAdvice =
                                resultJson?.payment_advice_data?.[0] || {};
                              const matchingDetails =
                                getPayPerUseMatchingDetails(normalizedCard);
                              const paymentSummary =
                                normalizedCard?.payment_advice_summary ||
                                resultJson?.payment_advice_summary ||
                                matchingDetails?.payment_advice_summary ||
                                {};
                              const vendorName =
                                po?.vendor ||
                                po?.vendor_name ||
                                po?.supplier_name ||
                                invoice?.vendor_name ||
                                invoice?.supplier_name ||
                                grn?.vendor_name ||
                                grn?.vendor ||
                                "—";
                              const poNumber =
                                inferPayPerUsePoNumber(normalizedCard);
                              const invoiceNumber =
                                inferPayPerUseInvoiceNumber(normalizedCard);
                              const grnNumber =
                                inferPayPerUseGrnNumber(normalizedCard);

                              const resolvedPoTotalAmt = formatPayPerUseAmount(
                                po?.total_tax_included ||
                                  po?.tax_included_total ||
                                  po?.total_with_tax ||
                                  po?.grand_total ||
                                  po?.total_amount ||
                                  po?.amount,
                              );
                              const resolvedInvoiceTotalAmt =
                                formatPayPerUseAmount(
                                  inferPayPerUseInvoiceTotal(normalizedCard),
                                );
                              const paymentRefNo =
                                paymentAdvice?.utr_rrn_no ||
                                paymentAdvice?.transaction_reference ||
                                paymentAdvice?.payment_reference ||
                                paymentSummary?.transaction_references?.join?.(", ") ||
                                paymentSummary?.payment_advice_numbers?.join?.(", ") ||
                                paymentAdvice?.payment_advice_number ||
                                "—";
                              const advancePaid = formatPayPerUseAmount(
                                invoice?.advance_paid ||
                                  invoice?.advance_payment ||
                                  invoice?.advance_amount ||
                                  paymentAdvice?.advance_paid ||
                                  paymentAdvice?.advance_payment ||
                                  paymentAdvice?.advance_amount ||
                                  paymentSummary?.advance_payment_amount ||
                                  paymentSummary?.advance_paid,
                              );
                              const balanceDue = formatPayPerUseAmount(
                                invoice?.balance_due ||
                                  invoice?.balance_amount ||
                                  paymentAdvice?.balance_due ||
                                  paymentAdvice?.balance_amount ||
                                  paymentSummary?.outstanding_amount,
                              );

                              const recordStatuses =
                                getPayPerUseRecordStatuses(normalizedCard);
                              const itemMatchStatus = recordStatuses.item;
                              const quantityMatchStatus =
                                recordStatuses.quantity;
                              const priceMatchStatus = recordStatuses.price;

                              const approvalMeta =
                                getTwmApprovalMeta(normalizedCard);
                              const approversArray = approvalMeta.approvers;
                              const overallStatus = approvalMeta.label;
                              const statusColor = approvalMeta.color;

                              const userCanApproveReject =
                                (isPayPerUse || (!approversLoading && !approversError)) &&
                                canUserApproveReject(normalizedCard);
                              const userApprovalStatus =
                                getUserApprovalStatus(normalizedCard);

                              return (
                                <TableRow
                                  key={idx}
                                  sx={{
                                    "& td": {
                                      py: 1.8,
                                      px: 1,
                                      borderBottom: "1px solid #f1f5f9",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      fontFamily: "gilroyMedium",
                                      fontSize: "14px",
                                      color: "#475569",
                                    },
                                  }}
                                >
                                  {/* Invoice Ref ID */}
                                  <TableCell
                                    sx={{
                                      fontFamily: "gilroyBold",
                                      color: "#000000",
                                      fontSize: "14px",
                                      width: listColumnWidths.matchCode,
                                      minWidth: listColumnWidths.matchCode,
                                      maxWidth: listColumnWidths.matchCode,
                                      position: "sticky",
                                      left: 0,
                                      zIndex: 2,
                                      fontWeight: 600,
                                      backgroundColor: "#f8fafc",
                                      boxShadow: "2px 0 0 #e2e8f0",
                                    }}
                                  >
                                    {normalizedCard.match_code || "—"}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.matchingType,
                                      minWidth: listColumnWidths.matchingType,
                                      maxWidth: listColumnWidths.matchingType,
                                    }}
                                  >
                                    {formatMatchingMode(
                                      normalizedCard.matching_mode,
                                    )}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.supplierName,
                                      minWidth: listColumnWidths.supplierName,
                                      maxWidth: listColumnWidths.supplierName,
                                    }}
                                  >
                                    <Tooltip
                                      title={vendorName}
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {vendorName}
                                      </Box>
                                    </Tooltip>
                                  </TableCell>

                                  {/* PO Number */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.poId,
                                      minWidth: listColumnWidths.poId,
                                      maxWidth: listColumnWidths.poId,
                                    }}
                                  >
                                    {poNumber}
                                  </TableCell>

                                  {/* PO Bill Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.poDate,
                                      minWidth: listColumnWidths.poDate,
                                      maxWidth: listColumnWidths.poDate,
                                    }}
                                  >
                                    {formatDate(po?.order_date || po?.po_date)}
                                  </TableCell>

                                  {/* Invoice Number */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.invoiceNumber,
                                      minWidth: listColumnWidths.invoiceNumber,
                                      maxWidth: listColumnWidths.invoiceNumber,
                                    }}
                                  >
                                    {invoiceNumber}
                                  </TableCell>

                                  {/* Invoice Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.invoiceDate,
                                      minWidth: listColumnWidths.invoiceDate,
                                      maxWidth: listColumnWidths.invoiceDate,
                                    }}
                                  >
                                    {formatDate(invoice?.invoice_date)}
                                  </TableCell>

                                  {/* GRN Number */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.grnNumber,
                                      minWidth: listColumnWidths.grnNumber,
                                      maxWidth: listColumnWidths.grnNumber,
                                    }}
                                  >
                                    {grnNumber}
                                  </TableCell>

                                  {/* GRN Bill Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.grnDate,
                                      minWidth: listColumnWidths.grnDate,
                                      maxWidth: listColumnWidths.grnDate,
                                    }}
                                  >
                                    {formatDate(
                                      grn?.bill_date ||
                                        grn?.created_at ||
                                        grn?.date,
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.paymentRefNo,
                                      minWidth: listColumnWidths.paymentRefNo,
                                      maxWidth: listColumnWidths.paymentRefNo,
                                    }}
                                  >
                                    {String(normalizedCard?.matching_mode || "").toUpperCase().includes("FOUR")
                                      ? paymentRefNo
                                      : "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.advancePaid,
                                      minWidth: listColumnWidths.advancePaid,
                                      maxWidth: listColumnWidths.advancePaid,
                                    }}
                                  >
                                    {String(normalizedCard?.matching_mode || "").toUpperCase().includes("FOUR")
                                      ? advancePaid
                                      : "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.balanceDue,
                                      minWidth: listColumnWidths.balanceDue,
                                      maxWidth: listColumnWidths.balanceDue,
                                    }}
                                  >
                                    {String(normalizedCard?.matching_mode || "").toUpperCase().includes("FOUR")
                                      ? balanceDue
                                      : "—"}
                                  </TableCell>

                                  {/* PO Grand Total */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.poTotal,
                                      minWidth: listColumnWidths.poTotal,
                                      maxWidth: listColumnWidths.poTotal,
                                    }}
                                  >
                                    {resolvedPoTotalAmt}
                                  </TableCell>

                                  {/* Invoice Grand Total */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.invoiceTotal,
                                      minWidth: listColumnWidths.invoiceTotal,
                                      maxWidth: listColumnWidths.invoiceTotal,
                                    }}
                                  >
                                    {resolvedInvoiceTotalAmt}
                                  </TableCell>

                                  {/* Item Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.itemStatus,
                                      minWidth: listColumnWidths.itemStatus,
                                      maxWidth: listColumnWidths.itemStatus,
                                    }}
                                  >
                                    <Tooltip
                                      title="Item matching analysis"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <span>
                                        <StatusChip
                                          status={itemMatchStatus.status}
                                          type={itemMatchStatus.type}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>

                                  {/* Quantity Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.quantityStatus,
                                      minWidth: listColumnWidths.quantityStatus,
                                      maxWidth: listColumnWidths.quantityStatus,
                                    }}
                                  >
                                    <Tooltip
                                      title="Quantity matching analysis"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <span>
                                        <StatusChip
                                          status={quantityMatchStatus.status}
                                          type={quantityMatchStatus.type}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>

                                  {/* Price Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.priceStatus,
                                      minWidth: listColumnWidths.priceStatus,
                                      maxWidth: listColumnWidths.priceStatus,
                                    }}
                                  >
                                    <Tooltip
                                      title="Price matching analysis"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <span>
                                        <StatusChip
                                          status={priceMatchStatus.status}
                                          type={priceMatchStatus.type}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>

                                  {/* Matching Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.matchingDate,
                                      minWidth: listColumnWidths.matchingDate,
                                      maxWidth: listColumnWidths.matchingDate,
                                    }}
                                  >
                                    {normalizedCard?.created_at
                                      ? format(
                                          new Date(normalizedCard.created_at),
                                          "dd MMM yyyy",
                                        )
                                      : "—"}
                                  </TableCell>

                                  {/* Approval Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.approvalStatus,
                                      minWidth: listColumnWidths.approvalStatus,
                                      maxWidth: listColumnWidths.approvalStatus,
                                  }}
                                >
                                    {!isPayPerUse && (approversLoading || approversError) ? (
                                      <Chip
                                        label={approversError ? "Approval details unavailable" : "Loading approvals..."}
                                        variant="outlined"
                                        size="small"
                                      />
                                    ) : overallStatus === "No Status" ? (
                                      <Chip
                                        label="No Status"
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          fontFamily: "gilroySemiBold",
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          height: "30px",
                                          backgroundColor: "#f8fafc",
                                          color: "#64748b",
                                        }}
                                      />
                                    ) : approversArray.length > 0 ? (
                                      <Tooltip
                                        title={
                                          <ApprovalStatusTooltip
                                            approvers={approversArray}
                                          />
                                        }
                                        arrow
                                        placement="top"
                                      >
                                        <Chip
                                          label={overallStatus}
                                          color={statusColor}
                                          variant="outlined"
                                          size="small"
                                          sx={{
                                            fontFamily: "gilroySemiBold",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            height: "30px",
                                            backgroundColor:
                                              overallStatus === "Approved"
                                                ? "#f0fdf4"
                                                : overallStatus === "Pending"
                                                  ? "#fffbeb"
                                                  : "#fef2f2",
                                            color:
                                              overallStatus === "Approved"
                                                ? "#166534"
                                                : overallStatus === "Pending"
                                                  ? "#d97706"
                                                  : "#dc2626",
                                          }}
                                        />
                                      </Tooltip>
                                    ) : (
                                      <Chip
                                        label={overallStatus}
                                        color={statusColor}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          fontFamily: "gilroySemiBold",
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          height: "30px",
                                          backgroundColor:
                                            overallStatus === "Approved"
                                              ? "#f0fdf4"
                                              : overallStatus === "Pending"
                                                ? "#fffbeb"
                                                : "#fef2f2",
                                          color:
                                            overallStatus === "Approved"
                                              ? "#166534"
                                              : overallStatus === "Pending"
                                                ? "#d97706"
                                                : "#dc2626",
                                        }}
                                      />
                                    )}
                                  </TableCell>

                                  {/* Approve/Reject Buttons */}
                                  {userIsApprover && (
                                    <TableCell
                                      sx={{
                                        position: "sticky",
                                        right: listColumnWidths.action,
                                        width: listColumnWidths.approveReject,
                                        minWidth:
                                          listColumnWidths.approveReject,
                                        maxWidth:
                                          listColumnWidths.approveReject,
                                        zIndex: 2,
                                        backgroundColor: "#f8fafc",
                                        borderLeft: "1px solid #e2e8f0",
                                        boxShadow:
                                          "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                                      }}
                                    >
                                      {userCanApproveReject ? (
                                        <Stack direction="row" spacing={0.5}>
                                          <Button
                                            variant="outlined"
                                            color="success"
                                            size="small"
                                            sx={{
                                              fontFamily: "gilroyMedium",
                                              textTransform: "none",
                                              fontWeight: 500,
                                              fontSize: "14px",
                                              px: 1,
                                              height: "34px",
                                            }}
                                            onClick={() =>
                                              approveAndRejectDoc(
                                                "accept",
                                                normalizedCard.token,
                                              )
                                            }
                                          >
                                            Approve
                                          </Button>
                                          <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            sx={{
                                              fontFamily: "gilroyMedium",
                                              textTransform: "none",
                                              fontWeight: 500,
                                              fontSize: "14px",
                                              px: 1,
                                              height: "34px",
                                            }}
                                            onClick={() =>
                                              approveAndRejectDoc(
                                                "reject",
                                                normalizedCard.token,
                                              )
                                            }
                                          >
                                            Reject
                                          </Button>
                                        </Stack>
                                      ) : userApprovalStatus ? (
                                        <Chip
                                          label={
                                            userApprovalStatus === "APPROVED"
                                              ? "Approved"
                                              : "Rejected"
                                          }
                                          size="small"
                                          sx={{
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color:
                                              userApprovalStatus === "APPROVED"
                                                ? "#fff"
                                                : "#fff",
                                            backgroundColor:
                                              userApprovalStatus === "APPROVED"
                                                ? "#47a34b"
                                                : "#dc2626",
                                            "& .MuiChip-label": {
                                              px: 1.5,
                                            },
                                          }}
                                        />
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          fontFamily="gilroyMedium"
                                          fontSize="14px"
                                        >
                                          No action required
                                        </Typography>
                                      )}
                                    </TableCell>
                                  )}

                                  {/* Actions */}
                                  <TableCell
                                    sx={{
                                      position: "sticky",
                                      right: 0,
                                      width: listColumnWidths.action,
                                      minWidth: listColumnWidths.action,
                                      maxWidth: listColumnWidths.action,
                                      zIndex: 4,
                                      backgroundColor: "#f8fafc",
                                      borderLeft: "1px solid #e2e8f0",
                                      boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        // gap: 0.5,
                                        justifyContent: "center",
                                        alignItems: "center",
                                        "& .MuiSvgIcon-root": { fontSize: 15 },
                                      }}
                                    >
                                      <Tooltip
                                        title="View record details"
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              fontSize: "13px",
                                              fontWeight: 500,
                                              px: 1.4,
                                              py: 0.9,
                                              borderRadius: 1.5,
                                            },
                                          },
                                        }}
                                      >
                                        <IconButton
                                          onClick={() =>
                                            handleViewItems(normalizedCard)
                                          }
                                          color="primary"
                                          sx={{
                                            width: 34,
                                            height: 34,
                                            p: 0,
                                            border: "1px solid #dbeafe",
                                            borderRadius: 1.5,
                                            backgroundColor: "#f8fbff",
                                            color: "#1d4ed8",
                                            "&:hover": {
                                              backgroundColor: "#eff6ff",
                                              borderColor: "#bfdbfe",
                                            },
                                          }}
                                        >
                                          <VisibilityIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                      </Tooltip>

                                      <DownloadMenuButton
                                        onCSV={() => downloadXLSX(card)}
                                        sx={{ minWidth: 0, p: 0.5 }}
                                      />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {tableLoading && (
                        <TableLoadingState message={tableLoadingMessage} />
                      )}

                      {/* Pagination (backend) */}
                      <Box
                        sx={{ borderTop: "1px solid #e2e8f0", px: 2, py: 1.5 }}
                      >
                        <PaginantionComponent
                          pageNumber={pageNumber}
                          currentPage={activePaginationData?.current_page || 1}
                          itemsPerPage={activePaginationData?.total_count || 0}
                          totalItems={activePaginationData?.page_count || 0}
                          previouPageClick={() => {
                            showTableTransitionLoader();
                            previouPageClick(
                              parseInt(pageNumber, 10) || 1,
                              setPageNumber,
                            );
                          }}
                          nextPageClick={() => {
                            showTableTransitionLoader();
                            nextPageClick(
                              parseInt(pageNumber, 10) || 1,
                              setPageNumber,
                              activePaginationData,
                            );
                          }}
                          setPageNumber={(value) => {
                            showTableTransitionLoader();
                            setPageNumber(value);
                          }}
                          disablePrevious={
                            tableLoading ||
                            parseInt(pageNumber, 10) === 1 || pageNumber === ""
                          }
                          disableNext={
                            tableLoading ||
                            parseInt(pageNumber, 10) ===
                              activePaginationData?.page_count ||
                            !activePaginationData ||
                            pageNumber === ""
                          }
                        />
                      </Box>
                    </Paper>
                  </Box>
                </Grid>

                {!isPayPerUse && (
                  <TWMOverviewDashboard
                    data={matchedresponse || []}
                    summaryData={summaryResponse}
                    loading={summaryLoading}
                    onRefresh={fetchDashboardSummary}
                    onExportDashboard={downloadDashboardReportCSV}
                  />
                )}
                {isPayPerUse && (
                  <PayAsYouGoDashboard
                    summaryData={summaryResponse}
                    loading={summaryLoading}
                  />
                )}
              </Grid>
            )}
          </>
        )}

        {/* UPLOAD PAGE: back button, upload panel, NO search field at all */}
        {mode === "upload" && (
          <Box
            sx={{
              ...enterprisePageSx,
              minWidth: "100%",
              position: "relative",
            }}
          >
            <>
              {/* Back button at top right */}
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => go("main")}
                sx={{
                  ...enterpriseBackButtonSx,
                  position: "absolute",
                  top: 32,
                  right: 32,
                  minWidth: "auto",
                  minHeight: "34px",
                  px: 1.25,
                  py: 0.5,
                }}
              >
                Back
              </Button>

              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2,
                  mt: 5,
                }}
              >
                <Typography
                  sx={{
                    ...enterpriseSectionTitleSx,
                    flexGrow: 1,
                  }}
                >
                  {isPayPerUse
                    ? "Bulk Document Extraction"
                    : `Upload ${TWM_REQUIRED_DOCUMENT_SHORT_LABEL}`}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={enterpriseSupportingTextSx}
                >
                  {isPayPerUse ? "" : "Matching Code"}
                </Typography>
                <Typography
                  sx={{
                    ...enterpriseBodyTextSx,
                    color: isPayPerUse ? ENTERPRISE_COLORS.secondaryText : ENTERPRISE_COLORS.primary,
                  }}
                >
                  {isPayPerUse
                    ? "Upload one or more documents to start bulk extraction."
                    : nextTwmCode || ""}
                </Typography>
              </Box>

              {/* Drag & Drop */}
              <Box
                ref={dropRef}
                sx={{ ...getEnterpriseUploadZoneSx(isDragging), mb: 3 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...enterpriseBodyTextSx, mb: 0.5 }}>
                    Drag and drop file here
                  </Typography>
                  <Typography sx={enterpriseSupportingTextSx}>
                    (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
                  </Typography>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={DOCUMENT_UPLOAD_ACCEPT}
                  onChange={handleFileChange}
                  onClick={(e) => (e.target.value = null)}
                  style={{ display: "none" }}
                />
                <Button
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                  sx={enterpriseBrowseButtonSx}
                >
                  Browse
                </Button>

                {uploadStatus === "uploading" && (
                  <Box sx={{ width: "100%" }}>
                    <LinearProgress
                      variant="determinate"
                      value={overallProgress}
                      sx={{ borderRadius: 1 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontFamily: "gilroyMedium",
                        fontSize: "14px",
                      }}
                    >
                      {overallProgress || 0}% uploaded
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Selected files list */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#5a6b85",
                        fontSize: "14px",
                        fontFamily: "gilroyMedium",
                      }}
                    >
                      {isPayPerUse
                        ? `Selected files (${selectedFiles.length})`
                        : `Selected files (${selectedFiles.length})`}
                    </Typography>

                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      disabled={
                        uploadStatus === "uploading" ||
                        uploadStatus === "processing"
                      }
                      onClick={handleClearAllFiles}
                      sx={{
                        fontFamily: "gilroyBold",
                        textTransform: "none",
                        fontSize: "13px",
                        minWidth: "auto",
                        px: 0.5,
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>

                  {isPayPerUse ? (
                    <Box
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 4,
                        border: "1px solid #d8e2f1",
                        background:
                          "linear-gradient(180deg, #ffffff 0%, #f8fbff 38%, #eef4fb 100%)",
                        boxShadow: "0 20px 44px rgba(15, 23, 42, 0.12)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 34, sm: 42 },
                              height: { xs: 34, sm: 42 },
                              borderRadius: 2.5,
                              display: "grid",
                              placeItems: "center",
                              background:
                                "linear-gradient(135deg, #0b63a9 0%, #2f80c3 100%)",
                              color: "#fff",
                              fontFamily: "GilroyBold",
                              fontSize: { xs: "11px", sm: "13px" },
                              boxShadow: "0 8px 20px rgba(11, 99, 169, 0.22)",
                            }}
                          >
                            DOC
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "GilroyBold",
                                fontSize: { xs: "18px", sm: "22px" },
                                color: "#0b63a9",
                              }}
                            >
                              Uploaded Document
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "gilroyMedium",
                                fontSize: "12px",
                                color: "#64748b",
                              }}
                            >
                              Invoices, POs, GRNs and Payment Advice can be
                              uploaded in any order.
                            </Typography>
                          </Box>
                        </Box>

                        <Chip
                          label={`${selectedFiles.length} Documents`}
                          size="small"
                          sx={{
                            backgroundColor: "#dbeafe",
                            color: "#1d4ed8",
                            fontFamily: "GilroyBold",
                            fontSize: "12px",
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          position: "relative",
                          minHeight: payPerUseBoardMinHeight,
                          maxHeight: selectedFiles.length > 20 ? 620 : "none",
                          overflow: "hidden",
                          borderRadius: 3,
                          border: "1px solid rgba(203, 213, 225, 0.75)",
                          background:
                            "radial-gradient(circle at 50% 8%, rgba(255,255,255,0.98), rgba(242,247,252,0.95) 42%, rgba(226,236,247,0.98) 100%)",
                          p: { xs: 1.5, sm: 2.5 },
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(180deg, rgba(255,255,255,0.40) 0%, rgba(255,255,255,0.08) 100%)",
                            pointerEvents: "none",
                          }}
                        />

                        <Box
                          sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: 26,
                            transform: "translateX(-50%)",
                            width: { xs: "88%", sm: "74%" },
                            height: { xs: 48, sm: 72 },
                            borderRadius: "50%",
                            background:
                              "radial-gradient(circle, rgba(148,163,184,0.30) 0%, rgba(148,163,184,0.12) 42%, rgba(255,255,255,0) 75%)",
                            filter: "blur(8px)",
                            pointerEvents: "none",
                          }}
                        />

                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "100%",
                            mx: "auto",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Box
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "repeat(2, minmax(0, 1fr))",
      lg: "minmax(320px, 2fr) minmax(220px, 1fr) minmax(160px, 0.75fr) minmax(150px, auto) minmax(170px, auto)",
    },
    alignItems: "center",
    gap: { xs: 1.25, md: 1.5 },
    p: { xs: 1.25, sm: 2 },
    width: "100%",
    "& input": {
      fontFamily: "gilroyMedium",
      fontSize: "14px",
    },
  }}
>
  <Box
    sx={{
      minWidth: 0,
      width: "100%",
      gridColumn: { xs: "auto", md: "1 / -1", lg: "auto" },
    }}
  >
    <SearchBar
      value={searchTerm}
      onChange={handleSearchChange}
      placeholder="Search by Matching ID, Supplier name, PO ID, Invoice ID, GRN ID..."
    />
  </Box>

  <Select
    multiple
    value={selectedSuppliers}
    onChange={(event) => handleSupplierChange(event.target.value)}
    size="small"
    displayEmpty
    renderValue={(selected) => {
      if (!selected.length) return "Supplier: All";

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            maxWidth: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            "&::-webkit-scrollbar": { height: 0 },
          }}
        >
          {selected.map((supplier) => (
            <Chip
              key={supplier}
              label={supplier}
              size="small"
              deleteIcon={<ClearIcon sx={{ fontSize: 15 }} />}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onDelete={(event) => {
                event.stopPropagation();
                removeSupplierFilter(supplier);
              }}
              sx={{
                maxWidth: 150,
                height: 26,
                flexShrink: 0,
                borderRadius: "14px",
                backgroundColor: "#eff6ff",
                color: "#1d4ed8",
                border: "1px solid #bfdbfe",
                fontFamily: "gilroyMedium",
                fontSize: "13px",
                "& .MuiChip-label": {
                  px: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiChip-deleteIcon": {
                  color: "#1d4ed8",
                  mr: 0.6,
                  "&:hover": {
                    color: "#dc2626",
                  },
                },
              }}
            />
          ))}
        </Box>
      );
    }}
    sx={{
      minHeight: 42,
      width: "100%",
      minWidth: 0,
      backgroundColor: "#ffffff",
      borderRadius: 2,
      fontFamily: "gilroyMedium",
      fontSize: "13px",
      "& .MuiSelect-select": {
        py: 0.75,
        pl: 1.25,
        display: "flex",
        alignItems: "center",
        minHeight: "26px",
      },
    }}
  >
    {supplierOptions.map((supplier) => (
      <MenuItem
        key={supplier}
        value={supplier}
        sx={{ fontSize: "13px", maxWidth: 320 }}
      >
        <ListItemText
          primary={supplier}
          primaryTypographyProps={{
            fontSize: "13px",
            fontFamily: "gilroyMedium",
            noWrap: true,
          }}
        />
      </MenuItem>
    ))}
  </Select>

  <Select
    value={approvalStatusFilter}
    onChange={(event) =>
      setApprovalStatusFilter(event.target.value)
    }
    size="small"
    displayEmpty
    sx={{
      height: 42,
      width: "100%",
      minWidth: 0,
      backgroundColor: "#ffffff",
      borderRadius: 2,
      fontFamily: "gilroyMedium",
      fontSize: "13px",
      "& .MuiSelect-select": {
        py: 1,
        pl: 1.25,
      },
    }}
  >
    <MenuItem value="all" sx={{ fontSize: "13px" }}>
      Status: All
    </MenuItem>
    <MenuItem value="PENDING" sx={{ fontSize: "13px" }}>
      Status: Pending
    </MenuItem>
    <MenuItem value="APPROVED" sx={{ fontSize: "13px" }}>
      Status: Approved
    </MenuItem>
    <MenuItem value="REJECTED" sx={{ fontSize: "13px" }}>
      Status: Rejected
    </MenuItem>
  </Select>

  <Button
    variant="outlined"
    startIcon={<DownloadIcon />}
    onClick={() => downloadAllXLSX(filterRecords)}
    sx={{
      fontFamily: "gilroyMedium",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 2,
      fontSize: "14px",
      minHeight: "42px",
      width: { xs: "100%", md: "100%", lg: "auto" },
      whiteSpace: "nowrap",
      justifySelf: { xs: "stretch", lg: "start" },
      "& .MuiButton-startIcon": { flexShrink: 0 },
    }}
  >
    Export Report
  </Button>

  {![ 
    "Finance Analyst",
    "Invoice Analyst",
    "Business Finance",
    "Account Payable",
  ].includes(user_designation) && (
    <Button
      variant="contained"
      color="primary"
      startIcon={<CloudUploadIcon />}
      onClick={async () => {
        if (!isPayPerUse) {
          await fetchNextCode();
        }
        go("upload");
      }}
      sx={{
        fontFamily: "gilroySemiBold",
        textTransform: "none",
        borderRadius: 1,
        fontSize: "14px",
        minHeight: "42px",
        px: 2,
        width: { xs: "100%", md: "100%", lg: "auto" },
        whiteSpace: "nowrap",
        justifySelf: { xs: "stretch", lg: "start" },
        "& .MuiButton-startIcon": { flexShrink: 0 },
      }}
    >
      {isPayPerUse ? "Bulk Upload & Match" : "Create Matching"}
    </Button>
  )}
</Box>

                          {payPerUseRemainingStackCount > 0 && (
                            <Box
                              sx={{
                                position: "absolute",
                                right: { xs: 12, sm: 18, md: 24 },
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: { xs: 88, sm: 104, md: 118 },
                                aspectRatio: "1 / 1.16",
                                zIndex: 3,
                              }}
                            >
                              {[0, 1, 2].map((layer) => (
                                <Box
                                  key={layer}
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    transform: `translate(${layer * 4}px, ${layer * 4}px)`,
                                    borderRadius: 2.2,
                                    background:
                                      layer === 2
                                        ? "linear-gradient(135deg, #0b63a9 0%, #2f80c3 100%)"
                                        : "linear-gradient(180deg, #e5eefb 0%, #d7e6fb 100%)",
                                    border:
                                      "1px solid rgba(147, 197, 253, 0.9)",
                                    boxShadow:
                                      layer === 2
                                        ? "0 16px 28px rgba(11, 99, 169, 0.18)"
                                        : "0 8px 18px rgba(15, 23, 42, 0.10)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: layer === 2 ? "#fff" : "transparent",
                                    overflow: "hidden",
                                  }}
                                >
                                  {layer === 2 && (
                                    <Box sx={{ textAlign: "center", px: 0.75 }}>
                                      <Typography
                                        sx={{
                                          fontFamily: "GilroyBold",
                                          fontSize: {
                                            xs: "18px",
                                            sm: "20px",
                                            md: "22px",
                                          },
                                          lineHeight: 1,
                                        }}
                                      >
                                        +{payPerUseRemainingStackCount}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          mt: 0.35,
                                          fontFamily: "gilroyMedium",
                                          fontSize: {
                                            xs: "12px",
                                            sm: "13px",
                                            md: "14px",
                                          },
                                          lineHeight: 1.15,
                                          opacity: 0.96,
                                        }}
                                      >
                                        more files
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 1.25,
                      }}
                    >
                      {selectedFiles.map((file, index) => (
                        <Box
                          key={`${file.name}-${file.lastModified}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            minWidth: 0,
                            p: 1.25,
                            borderRadius: 2,
                            background:
                              "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                            border: "1px solid #dbe5f0",
                            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                          }}
                        >
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              flex: "0 0 42px",
                              borderRadius: 1.5,
                              display: "grid",
                              placeItems: "center",
                              background: "#eaf3ff",
                              color: "#0b63a9",
                              border: "1px solid #c8ddf6",
                            }}
                          >
                            <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
                          </Box>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              title={file.name}
                              sx={{
                                fontFamily: "GilroyBold",
                                fontSize: "13px",
                                color: "#172033",
                              }}
                            >
                              {file.name}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ mt: 0.75 }}
                            >
                              <Chip
                                label={
                                  file.name?.includes(".")
                                    ? file.name.split(".").pop().toUpperCase()
                                    : "FILE"
                                }
                                size="small"
                                sx={{
                                  height: 22,
                                  borderRadius: 1,
                                  backgroundColor: "#eef6ff",
                                  color: "#0b63a9",
                                  fontFamily: "gilroyBold",
                                  fontSize: "11px",
                                  "& .MuiChip-label": { px: 0.75 },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: "gilroyMedium",
                                  fontSize: "12px",
                                  color: "#64748b",
                                }}
                              >
                                {formatKB(file.size)}
                              </Typography>
                            </Stack>
                          </Box>

                          <Tooltip
                            title="Remove"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "10px",
                                },
                              },
                            }}
                          >
                            <span>
                              <IconButton
                                aria-label="Remove file"
                                disabled={
                                  uploadStatus === "uploading" ||
                                  uploadStatus === "processing"
                                }
                                onClick={() => handleRemoveFile(index)}
                                size="small"
                                sx={{
                                  flex: "0 0 auto",
                                  color: "#ef4444",
                                  backgroundColor: "#fff1f2",
                                  "&:hover": {
                                    backgroundColor: "#ffe4e6",
                                  },
                                }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Action buttons */}
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mt: 2 }}
              >
                {statusChip}
                <Dbutton
                  label={
                    uploadStatus === "uploading"
                      ? "Uploading..."
                      : isPayPerUse
                        ? "Start Extraction"
                        : "Upload Documents"
                  }
                  onClick={handleUpload}
                  loadingState={uploadStatus === "uploading"}
                  disabled={
                    selectedFiles.length === 0 ||
                    (!isPayPerUse &&
                      !hasAllowedTwmFileCount(selectedFiles.length)) ||
                    uploadStatus === "uploading" ||
                    uploadStatus === "processing" ||
                    (!isPayPerUse && canSaveMatching) ||
                    !selectedFiles.every(isDocumentUploadFile)
                  }
                />
                {uploadStatus === "uploading" && (
                  <Dbutton label="Cancel" onClick={handleCancelUpload} />
                )}
                {!isPayPerUse && (
                  <Dbutton
                    label={
                      saving ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={16} color="inherit" />
                          Saving...
                        </Stack>
                      ) : (
                        " Save Result"
                      )
                    }
                    onClick={handleSaveMatchingWithLoader}
                    disabled={
                      !hasAllowedTwmFileCount(selectedFiles.length) ||
                      uploadStatus === "uploading" ||
                      !canSaveMatching ||
                      saving
                    }
                  />
                )}
              </Stack>

              {/* Shimmer / placeholder while parsing */}
              {(uploadStatus === "uploading" ||
                uploadStatus === "processing") &&
                !response &&
                !payPerUseResults && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress />
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "gilroyMedium",
                        fontSize: "14px",
                        color: "text.secondary",
                      }}
                    >
                      {isPayPerUse
                        ? "Uploading and tracking extraction progress..."
                        : "Parsing documents and preparing results..."}
                    </Typography>
                  </Box>
                )}
            </>

            {/* Results */}
            {!isPayPerUse && response && (
              <Box sx={{ mt: 3 }}>
                <MatchResultInsight
                  poData={response.po_data}
                  invoiceData={response.invoice_data}
                  grnData={response.grn_data}
                  paymentAdviceData={response.payment_advice_data}
                  matchingDetails={{ ...response.matching_details, ...response }}
                  expectedDocumentCount={selectedFiles.length}
                />
                <ThreeWayMatchTable
                  poData={response.po_data}
                  invoiceData={response.invoice_data}
                  grnData={response.grn_data}
                  paymentAdviceData={response.payment_advice_data}
                  matchingDetails={{ ...response.matching_details, ...response }}
                  showSummary={true}
                />
              </Box>
            )}

            {isPayPerUse && (payPerUseStatus || payPerUseResults) && (
              <Box sx={{ mt: 3 }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Stack spacing={2.5}>
                    <Box>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        spacing={1.5}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: "GilroyBold",
                              fontSize: "18px",
                              color: "#0f172a",
                            }}
                          >
                            Batch Status
                          </Typography>
                        </Box>
                        <StatusChip
                          status={formatPayPerUseStatusLabel(
                            payPerUseResults?.status || payPerUseStatus?.status,
                          )}
                          type={getPayPerUseStatusTone(
                            payPerUseResults?.status || payPerUseStatus?.status,
                          )}
                        />
                      </Stack>

                      <Box sx={{ mt: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(
                            0,
                            Math.min(100, overallProgress || 0),
                          )}
                          sx={{ height: 10, borderRadius: 999 }}
                        />
                        <Typography
                          sx={{
                            mt: 1,
                            fontFamily: "gilroyMedium",
                            fontSize: "13px",
                            color: "#475569",
                          }}
                        >
                          {Math.round(overallProgress || 0)}% complete
                          {payPerUseStatus?.processed_files != null &&
                            payPerUseStatus?.total_files != null &&
                            ` • ${payPerUseStatus.processed_files}/${payPerUseStatus.total_files} files processed`}
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      {[
                        {
                          label: "Total Files",
                          value:
                            payPerUseResults?.total_files ??
                            payPerUseStatus?.total_files ??
                            0,
                        },
                        {
                          label: "Processed",
                          value:
                            payPerUseResults?.processed_files ??
                            payPerUseStatus?.processed_files ??
                            0,
                        },
                        {
                          label: "Successful",
                          value:
                            payPerUseResults?.successful_files ??
                            payPerUseStatus?.successful_files ??
                            0,
                        },
                        {
                          label: "Failed",
                          value:
                            payPerUseResults?.failed_files ??
                            payPerUseStatus?.failed_files ??
                            0,
                        },
                        {
                          label: "Groups Found",
                          value:
                            payPerUseResults?.groups_found ??
                            payPerUseStatus?.groups_found ??
                            0,
                        },
                      ].map((item) => (
                        <Grid item xs={6} sm={4} md={3} key={item.label}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              height: "100%",
                              borderColor: "#dbeafe",
                              backgroundColor: "#f8fbff",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "gilroyMedium",
                                fontSize: "13px",
                                color: "#64748b",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.5,
                                fontFamily: "GilroyBold",
                                fontSize: "20px",
                                color: "#0f172a",
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    {(payPerUseResults || payPerUseHasCompletedStatus) && (
                      <>
                        <Alert severity="info" sx={{ fontSize: "14px", fontFamily: "gilroyMedium" }}>
                          Review the extracted groups and save them once you are
                          ready.
                        </Alert>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Dbutton
                            label={
                              saving ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <CircularProgress size={16} color="inherit" />
                                  Saving...
                                </Stack>
                              ) : (
                                "Save Records"
                              )
                            }
                            onClick={handleSaveMatchingWithLoader}
                            disabled={!canSavePayPerUseMatching || saving}
                          />
                        </Box>

                        {Array.isArray(
                          payPerUseResultPayload.validation_errors,
                        ) &&
                          payPerUseResultPayload.validation_errors.length >
                            0 && (
                            <Alert severity="warning">
                              Validation errors:{" "}
                              {payPerUseResultPayload.validation_errors.join(
                                ", ",
                              )}
                            </Alert>
                          )}

                        {Array.isArray(payPerUseResultPayload.failed_files) &&
                          payPerUseResultPayload.failed_files.length > 0 && (
                            <Alert severity="error">
                              Failed files:{" "}
                              {payPerUseResultPayload.failed_files.join(", ")}
                            </Alert>
                          )}

                        {Array.isArray(
                          payPerUseResultPayload.ungrouped_documents,
                        ) &&
                          payPerUseResultPayload.ungrouped_documents.length >
                            0 && (
                            <Alert severity="warning">
                              Ungrouped documents:{" "}
                              {payPerUseResultPayload.ungrouped_documents.join(
                                ", ",
                              )}
                            </Alert>
                          )}

                        <Box>
                          <Typography
                            sx={{
                              fontFamily: "GilroyBold",
                              fontSize: "17px",
                              color: "#0f172a",
                              mb: 1.5,
                            }}
                          >
                            Grouped Results
                          </Typography>
                          <Typography
                            sx={{
                              mb: 2,
                              fontFamily: "gilroyMedium",
                              fontSize: "13px",
                              color: "#64748b",
                            }}
                          >
                            Review each grouped match before saving the records.
                          </Typography>

                          {payPerUseGroupedResults.length > 0 ? (
                            <Stack spacing={2}>
                              {payPerUseGroupedResults.map(
                                (group, index) => (
                                  <Paper
                                    key={`${group?.twm_token || "group"}-${index}`}
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      borderColor: "#e2e8f0",
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        borderColor: "#cbd5e1",
                                        boxShadow:
                                          "0 8px 20px rgba(15, 23, 42, 0.06)",
                                      },
                                    }}
                                  >
                                    <Stack
                                      direction={{ xs: "column", md: "row" }}
                                      justifyContent="space-between"
                                      alignItems={{ xs: "flex-start", md: "center" }}
                                      spacing={1.5}
                                    >
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontFamily: "GilroyBold",
                                            fontSize: "16px",
                                            color: "#0f172a",
                                          }}
                                        >
                                          {group?.twm_code ||
                                            `Group ${index + 1}`}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            mt: 0.5,
                                            fontFamily: "gilroyMedium",
                                            fontSize: "13px",
                                            color: "#64748b",
                                          }}
                                        >
                                          {Array.isArray(group?.files)
                                            ? group.files.join(", ")
                                            : "-"}
                                        </Typography>
                                      </Box>
                                      <StatusChip
                                        status={
                                          getPayPerUseOverallMatchStatus(group)
                                            .status
                                        }
                                        type={
                                          getPayPerUseOverallMatchStatus(group)
                                            .type
                                        }
                                      />
                                    </Stack>

                                    <Grid
                                      container
                                      spacing={2}
                                      sx={{ mt: 0.5 }}
                                    >
                                      <Grid item xs={12} sm={6} md={3}>
                                        <Typography
                                          sx={{
                                            fontFamily: "gilroyMedium",
                                            fontSize: "13px",
                                            color: "#64748b",
                                          }}
                                        >
                                          PO Number
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontFamily: "GilroyBold",
                                            fontSize: "14px",
                                            color: "#0f172a",
                                          }}
                                        >
                                          {inferPayPerUsePoNumber(group)}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} sm={6} md={3}>
                                        <Typography
                                          sx={{
                                            fontFamily: "gilroyMedium",
                                            fontSize: "13px",
                                            color: "#64748b",
                                          }}
                                        >
                                          Invoice Number
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontFamily: "GilroyBold",
                                            fontSize: "14px",
                                            color: "#0f172a",
                                          }}
                                        >
                                          {inferPayPerUseInvoiceNumber(group)}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} sm={6} md={3}>
                                        <Typography
                                          sx={{
                                            fontFamily: "gilroyMedium",
                                            fontSize: "13px",
                                            color: "#64748b",
                                          }}
                                        >
                                          GRN Number
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontFamily: "GilroyBold",
                                            fontSize: "14px",
                                            color: "#0f172a",
                                          }}
                                        >
                                          {inferPayPerUseGrnNumber(group)}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6} md={3}>
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          justifyContent={{
                                            xs: "flex-start",
                                            md: "flex-end",
                                          }}
                                          sx={{
                                            height: "100%",
                                            minHeight: 42,
                                          }}
                                        >
                                          <Tooltip
                                            title="View grouped record details"
                                            componentsProps={{
                                              tooltip: {
                                                sx: {
                                                  fontSize: "13px",
                                                  px: 1.4,
                                                  py: 0.9,
                                                  borderRadius: 1.5,
                                                },
                                              },
                                            }}
                                          >
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              startIcon={
                                                <VisibilityIcon
                                                  sx={{ fontSize: "18px" }}
                                                />
                                              }
                                              onClick={() =>
                                                handleOpenPayPerUsePreview(
                                                  group,
                                                )
                                              }
                                              sx={{
                                                height: 34,
                                                px: 1.5,
                                                border: "1px solid #dbeafe",
                                                borderRadius: 1.5,
                                                backgroundColor: "#f8fbff",
                                                color: "#1d4ed8",
                                                fontFamily: "gilroyMedium",
                                                fontSize: "13px",
                                                fontWeight: 400,
                                                textTransform: "none",
                                                "&:hover": {
                                                  backgroundColor: "#eff6ff",
                                                  borderColor: "#bfdbfe",
                                                },
                                              }}
                                            >
                                              View
                                            </Button>
                                          </Tooltip>
                                        </Stack>
                                      </Grid>
                                    </Grid>

                                    {Array.isArray(group?.mismatch_reasons) &&
                                      group.mismatch_reasons.length > 0 && (
                                        <Box sx={{ mt: 1.5 }}>
                                          <Typography
                                            sx={{
                                              fontFamily: "GilroyBold",
                                              fontSize: "14px",
                                              color: "#991b1b",
                                              mb: 1,
                                            }}
                                          >
                                            Mismatch Reasons
                                          </Typography>
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            useFlexGap
                                            flexWrap="wrap"
                                          >
                                            {group.mismatch_reasons.map(
                                              (reason, reasonIndex) => (
                                                <Chip
                                                  key={`${group?.twm_token || index}-${reasonIndex}`}
                                                  label={reason}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: "#fef2f2",
                                                    color: "#991b1b",
                                                    fontFamily:
                                                      "gilroySemiBold",
                                                    fontSize: "13px",
                                                    // fontWeight: "bold",
                                                    minHeight: "34px",
                                                    "& .MuiChip-label": {
                                                      px: 1.2,
                                                      py: 0.4,
                                                    },
                                                  }}
                                                />
                                              ),
                                            )}
                                          </Stack>
                                        </Box>
                                      )}
                                  </Paper>
                                ),
                              )}
                            </Stack>
                          ) : payPerUseHasCompletedStatus &&
                            Number(
                              payPerUseResults?.groups_found ??
                                payPerUseStatus?.groups_found ??
                                0,
                            ) > 0 ? (
                            <Alert
                              severity="info"
                              sx={{
                                fontFamily: "gilroyMedium",
                                fontSize: "14px",
                              }}
                              icon={<CircularProgress size={18} />}
                            >
                              Group count is available, but detailed grouped responses are still loading. Please wait a moment.
                            </Alert>
                          ) : (
                            <Alert severity="info" sx={{ fontSize: "14px", fontFamily: "gilroyMedium" }}>
                              No grouped responses are available yet. Upload status is shown above, and any validation, failed, or ungrouped documents will appear here as soon as the API returns them.
                            </Alert>
                          )}
                        </Box>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Box>
            )}
          </Box>
        )}

        {mode === "view" && (
          <Box sx={{ p: { xs: 2, sm: 3 }, position: "relative" }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => go("main")}
              sx={{
                position: "absolute",
                top: 0,
                right: 22,
                fontFamily: "gilroyBold",
                fontSize: { xs: "14px", sm: "14px" },
                minHeight: "44px",
                px: 2.25,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Back
            </Button>

            <Typography
              variant="subtitle2"
              sx={{ fontSize: "13px", color: "text.secondary", mb: 2 }}
            >
              Code:{" "}
              <strong style={{ color: "#1976d2" }}>
                {savedTwmCode || "—"}
              </strong>
            </Typography>

            {!viewData ? (
              <Alert severity="info" sx={{ fontSize: "14px", fontFamily: "gilroyMedium" }}>
                No saved matching response was found for this record.
              </Alert>
            ) : (
              <>
                <MatchResultInsight
                  poData={viewData.poData}
                  invoiceData={viewData.invoiceData}
                  grnData={viewData.grnData}
                />
                <ThreeWayMatchTable
                  poData={viewData.poData}
                  invoiceData={viewData.invoiceData}
                  grnData={viewData.grnData}
                  paymentAdviceData={viewData.paymentAdviceData}
                />
              </>
            )}
          </Box>
        )}

        {selectedCard && (
          <ThreeWayMatchTable
            poData={selectedCard.po_data}
            grnData={selectedCard.grn_data}
            invoiceData={selectedCard.invoice_data}
            paymentAdviceData={selectedCard.payment_advice_data}
            matchingDetails={{ ...selectedCard.matching_details, ...selectedCard }}
            selectedCard={selectedCard}
            open={viewItemsDialog}
            onClose={() => {
              setViewItemsDialog(false);
              setSelectedCard(null);
            }}
            showSummary={false} // <-- ADD THIS LINE
            fullPageView={true}
          />
        )}

        <Dialog
          open={Boolean(selectedPayPerUseGroup)}
          onClose={handleClosePayPerUsePreview}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "GilroyBold",
                    fontSize: "20px",
                    color: "#0f172a",
                    lineHeight: 1.2,
                  }}
                >
                  Matched Record Preview
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontFamily: "gilroyMedium",
                    fontSize: "13px",
                    color: "#64748b",
                  }}
                >
                  Document details, backend statuses, and extracted line items
                </Typography>
              </Box>
              <IconButton
                onClick={handleClosePayPerUsePreview}
                sx={{
                  width: 34,
                  height: 34,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  color: "#475569",
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                <ClearIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </DialogTitle>

          <DialogContent sx={{ p: 0, backgroundColor: "#f8fafc" }}>
            {selectedPayPerUseGroup && (
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={2}>
                  {(() => {
                    const previewResultJson = parsePayPerUseResultJson(
                      selectedPayPerUseGroup?.result_json ||
                        selectedPayPerUseGroup?.extracted_data,
                    );
                    const previewMatchingDetails = getPayPerUseMatchingDetails(
                      selectedPayPerUseGroup,
                    );
                    const previewPo = getPayPerUseFirstRecord(
                      previewResultJson?.po_data,
                    );
                    const previewInvoice = getPayPerUseFirstRecord(
                      previewResultJson?.invoice_data,
                    );
                    const previewGrn = getPayPerUseFirstRecord(
                      previewResultJson?.grn_data,
                    );
                    const poRows = buildPayPerUsePreviewRows(
                      previewPo?.line_items,
                      "PO",
                    );
                    const invoiceRows = buildPayPerUsePreviewRows(
                      previewInvoice?.line_items,
                      "INVOICE",
                    );
                    const grnRows = buildPayPerUsePreviewRows(
                      previewGrn?.line_items,
                      "GRN",
                    );

                    const renderPreviewTable = (title, rows, extraLabel) => (
                      <Paper
                        variant="outlined"
                        sx={{
                          borderColor: "#e2e8f0",
                          borderRadius: 2,
                          overflow: "hidden",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <Typography
                          sx={{
                            px: 2,
                            py: 1.4,
                            fontFamily: "GilroyBold",
                            fontSize: "15px",
                            color: "#0f172a",
                            borderBottom: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          {title}
                        </Typography>

                        {rows.length > 0 ? (
                          <TableContainer
                            sx={{
                              maxHeight: 300,
                            }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow
                                  sx={{
                                    backgroundColor: "#f8fafc",
                                    "& th": {
                                      fontFamily: "GilroyBold",
                                      fontSize: "13px",
                                      color: "#0f172a",
                                      whiteSpace: "nowrap",
                                      borderBottom: "1px solid #e2e8f0",
                                    },
                                  }}
                                >
                                  <TableCell>S.No</TableCell>
                                  <TableCell>Part No</TableCell>
                                  <TableCell>Description</TableCell>
                                  <TableCell>Qty</TableCell>
                                  <TableCell>UOM</TableCell>
                                  <TableCell>Rate</TableCell>
                                  <TableCell>Amount</TableCell>
                                  <TableCell>{extraLabel}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.map((row) => (
                                  <TableRow
                                    key={`${title}-${row.id}`}
                                    sx={{
                                      "& td": {
                                        fontFamily: "gilroyMedium",
                                        fontSize: "13px",
                                        color: "#1e293b",
                                        verticalAlign: "top",
                                        whiteSpace: "nowrap",
                                        borderBottom: "1px solid #f1f5f9",
                                      },
                                    }}
                                  >
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>{row.partNo}</TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>{row.qty}</TableCell>
                                    <TableCell>{row.uom}</TableCell>
                                    <TableCell>{row.rate}</TableCell>
                                    <TableCell>{row.amount}</TableCell>
                                    <TableCell>{row.extra}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Alert
                            severity="info"
                            sx={{
                              m: 2,
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                            }}
                          >
                            No {title.toLowerCase()} response data is available.
                          </Alert>
                        )}
                      </Paper>
                    );

                    return (
                      <>
                        <Box
                          sx={{
                            p: { xs: 2, sm: 2.25 },
                            borderRadius: 2,
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", md: "center" }}
                            spacing={2}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontFamily: "GilroyBold",
                                  fontSize: "18px",
                                  color: "#0f172a",
                                  lineHeight: 1.25,
                                }}
                              >
                                {selectedPayPerUseGroup?.twm_code ||
                                  "Grouped Match"}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.5,
                                  fontFamily: "gilroyMedium",
                                  fontSize: "13px",
                                  color: "#64748b",
                                  wordBreak: "break-word",
                                }}
                              >
                                {Array.isArray(selectedPayPerUseGroup?.files)
                                  ? selectedPayPerUseGroup.files.join(", ")
                                  : "-"}
                              </Typography>
                            </Box>
                            <StatusChip
                              status={
                                getPayPerUseOverallMatchStatus(
                                  selectedPayPerUseGroup,
                                ).status
                              }
                              type={
                                getPayPerUseOverallMatchStatus(
                                  selectedPayPerUseGroup,
                                ).type
                              }
                            />
                          </Stack>
                        </Box>

                        <Grid container spacing={1.5}>
                          {[
                            {
                              label: "PO Number",
                              value: inferPayPerUsePoNumber(
                                selectedPayPerUseGroup,
                              ),
                            },
                            {
                              label: "GRN Number",
                              value: inferPayPerUseGrnNumber(
                                selectedPayPerUseGroup,
                              ),
                            },
                            {
                              label: "Invoice Number",
                              value: inferPayPerUseInvoiceNumber(
                                selectedPayPerUseGroup,
                              ),
                            },
                            // {
                            //   label: "Compared Items",
                            //   value: Array.isArray(
                            //     selectedPayPerUseGroup?.item_comparisons,
                            //   )
                            //     ? selectedPayPerUseGroup.item_comparisons.length
                            //     : 0,
                            // },
                          ].map((item) => (
                            <Grid item xs={12} sm={4} key={item.label}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  height: "100%",
                                  borderColor: "#e2e8f0",
                                  backgroundColor: "#ffffff",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: "gilroyMedium",
                                    fontSize: "13px",
                                    color: "#64748b",
                                  }}
                                >
                                  {item.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.5,
                                    fontFamily: "GilroyBold",
                                    fontSize: "14px",
                                    color: "#0f172a",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {item.value}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>

                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderColor: "#e2e8f0",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <Grid container spacing={1.5} alignItems="center">
                            {[
                              {
                                label: "Overall",
                                value: previewMatchingDetails?.overall_status,
                              },
                              {
                                label: "Item",
                                value:
                                  previewMatchingDetails?.status_summary
                                    ?.item_status,
                              },
                              {
                                label: "Quantity",
                                value:
                                  previewMatchingDetails?.status_summary
                                    ?.quantity_status,
                              },
                              {
                                label: "Amount",
                                value:
                                  previewMatchingDetails?.status_summary
                                    ?.amount_status,
                              },
                            ].map((statusItem) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                key={statusItem.label}
                              >
                                <Stack spacing={0.75}>
                                  <Typography
                                    sx={{
                                      fontFamily: "gilroyMedium",
                                      fontSize: "13px",
                                      color: "#64748b",
                                    }}
                                  >
                                    {statusItem.label}
                                  </Typography>
                                  <Box>
                                    <StatusChip
                                      status={formatPayPerUseStatusLabel(
                                        statusItem.value,
                                      )}
                                      type={getPayPerUseStatusTone(
                                        statusItem.value,
                                      )}
                                    />
                                  </Box>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>

                        <MatchResultInsight
                          poData={previewResultJson?.po_data || []}
                          invoiceData={previewResultJson?.invoice_data || []}
                          grnData={previewResultJson?.grn_data || []}
                          matchingDetails={previewMatchingDetails}
                        />

                        <ThreeWayMatchTable
                          poData={previewResultJson?.po_data || []}
                          invoiceData={previewResultJson?.invoice_data || []}
                          grnData={previewResultJson?.grn_data || []}
                          paymentAdviceData={
                            previewResultJson?.payment_advice_data || []
                          }
                          matchingDetails={previewMatchingDetails}
                          showSummary={true}
                        />

                        {renderPreviewTable("PO Details", poRows, "HSN / IGST")}
                        {renderPreviewTable(
                          "Invoice Details",
                          invoiceRows,
                          "Batch / Packing",
                        )}
                        {renderPreviewTable(
                          "GRN Details",
                          grnRows,
                          "Lot / Expiry",
                        )}

                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderColor: "#e2e8f0",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <Typography
                            sx={{
                              mb: 1,
                              fontFamily: "GilroyBold",
                              fontSize: "14px",
                              color: "#0f172a",
                            }}
                          >
                            Files In This Match
                          </Typography>
                          <Stack spacing={1}>
                            {(selectedPayPerUseGroup?.files || []).map(
                              (fileName, fileIndex) => (
                                <Paper
                                  key={`${fileName}-${fileIndex}`}
                                  variant="outlined"
                                  sx={{
                                    p: 1.25,
                                    borderRadius: 1.5,
                                    borderColor: "#e2e8f0",
                                    backgroundColor: "#f8fafc",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: "gilroyMedium",
                                      fontSize: "14px",
                                      color: "#0f172a",
                                    }}
                                  >
                                    {fileName}
                                  </Typography>
                                </Paper>
                              ),
                            )}
                          </Stack>
                        </Paper>

                        {Array.isArray(
                          selectedPayPerUseGroup?.mismatch_reasons,
                        ) &&
                          selectedPayPerUseGroup.mismatch_reasons.length >
                            0 && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderColor: "#fecaca",
                                backgroundColor: "#fff7f7",
                              }}
                            >
                              <Typography
                                sx={{
                                  mb: 1,
                                  fontFamily: "GilroyBold",
                                  fontSize: "14px",
                                  color: "#991b1b",
                                }}
                              >
                                Mismatch Reasons
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                              >
                                {selectedPayPerUseGroup.mismatch_reasons.map(
                                  (reason, reasonIndex) => (
                                    <Chip
                                      key={`${reason}-${reasonIndex}`}
                                      label={reason}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#fef2f2",
                                        color: "#991b1b",
                                        fontFamily: "gilroySemiBold",
                                        fontSize: "13px",
                                        whiteSpace: "nowrap",
                                        minHeight: "34px",
                                        "& .MuiChip-label": {
                                          px: 1.2,
                                          py: 0.4,
                                        },
                                      }}
                                    />
                                  ),
                                )}
                              </Stack>
                            </Paper>
                          )}

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            pt: 1,
                            fontSize: "14px",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={handleClosePayPerUsePreview}
                            sx={{
                              fontFamily: "GilroyBold",
                              textTransform: "none",
                              borderRadius: 2,
                              px: 3,
                              fontSize: "14px",
                            }}
                          >
                            Close
                          </Button>
                        </Box>
                      </>
                    );
                  })()}
                </Stack>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        <Modal
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
        >
          <Fade in={showSuccessDialog}>
            <Box>
              <SuccessPopupDashboard
                popupImage={successPopupImg}
                popupHeroText="Matching Saved!"
                popupDescription={`${successDialogMessage}<br /><br /><b>Code:</b> ${
                  savedTwmCode || "-"
                }`}
                popBtn={true}
                btnText={"Okay"}
                btnClick={() => {
                  setShowSuccessDialog(false);
                  go("main");
                }}
                buyerBtn={true}
                buyerBtnOutline={true}
              />
            </Box>
          </Fade>
        </Modal>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.type}
            variant="filled"
            sx={{
              width: "100%",
              "& .MuiAlert-message": {
                fontSize: LARGE_SNACK_MESSAGES.has(snack.msg) ? "16px" : "14px",
                fontFamily: "gilroyMedium",
                fontWeight: LARGE_SNACK_MESSAGES.has(snack.msg) ? 600 : 500,
              },
            }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>

      <Modal open={openOfflinePopup}>
        <Fade in={openOfflinePopup}>
          <Box>
            <SuccessPopupDashboard
              popupImage={deletePopupImg}
              popupHeroText="Offline Mode"
              popupDescription="Data will sync once internet is available."
              popBtn={true}
              btnText={"Okay"}
              vendorBtn={true}
              btnClick={() => setOpenOfflinePopup(false)}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default ThreeWayMatching;
